import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef } from 'react';
import { Expert, Chat, Message, AppState, UIState, FileAttachment, GeneratedImage, PromptVaultItem, UiStreamEvent } from '../types';
import { apiService, API_BASE_URL } from '../services/api';
import { chatPersistenceService } from '../services/chatPersistence';
import { createAuthenticatedRequestOptions } from '../utils/apiUtils';
import { validateMessage, getModelFromExpert, ValidationResult } from '../utils/messageValidation';

interface ChatContextType {
  appState: AppState;
  uiState: UIState;
  loadExperts: () => Promise<void>;
  sendMessage: (message: string, attachedFiles?: File[], optimistic?: boolean) => Promise<{ requestId: string, chatId: string, expert: Expert } | undefined>;
  retryMessage: (messageIndex: number) => Promise<void>;
  editAndResendMessage: (userMessageIndex: number, newContent: string) => Promise<void>;
  loadChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => void;
  deleteMultipleChats: (chatIds: string[]) => Promise<void>;
  renameChat: (chatId: string, newTitle: string) => void;
  toggleChatStar: (chatId: string) => void;
  setSelectedExpert: (expert: Expert | null) => void;
  switchToLanding: () => void;
  switchToChat: () => void;
  toggleSidebar: () => void;
  toggleRightSidebar: () => void;
  addAttachedFile: (file: File) => void;
  removeAttachedFile: (index: number) => void;
  clearAttachedFiles: () => void;
  showLoading: (message: string) => void;
  hideLoading: () => void;
  showError: (message: string) => void;
  hideError: () => void;
  stopResponsePolling: () => void;
  filterExperts: (query: string) => void;
  showAllChatsView: () => void;
  hideAllChatsView: () => void;
  showSidebarLoading: () => void;
  hideSidebarLoading: () => void;
  registerSpacerUpdateCallback: (callback: () => void) => void;
  unregisterSpacerUpdateCallback: () => void;
  setOperationStatus: (status: '' | 'securing-transfer' | 'uploading-attachments' | 'thinking') => void;
  getFileUploadProgress: (fileName: string) => number;
  isFileUploadComplete: (fileName: string) => boolean;
  areAllUploadsComplete: () => boolean;
  clearFileUploadProgress: () => void;
  hasUploadsInProgress: () => boolean;
  validateCurrentMessage: (messageText: string) => Promise<ValidationResult>;
  clearValidationError: () => void;
  // PromptVault methods
  loadPromptVault: () => Promise<void>;
  savePromptToVault: (name: string, content: string, tags?: string[], sourceChatId?: string) => Promise<void>;
  deletePromptFromVault: (name: string) => Promise<void>;
  searchPromptVault: (searchQuery: string, limit?: number) => Promise<PromptVaultItem[]>;
  updatePromptUsage: (name: string) => Promise<void>;
  togglePromptStar: (name: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Helper function to generate UUID
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const initialAppState: AppState = {
  selectedExperts: new Map(),
  experts: [],
  filteredExperts: [],
  savedChats: [],
  currentChat: null,
  isProcessing: false,
  isLoadingChats: false,
  currentPollTimeout: null,
  shouldStopPolling: false,
  typewriterInterval: null,
  isTyping: false,
  fullContentBuffer: '',
  displayedContent: '',
  currentExpertMessageId: '',
  currentRequestId: '',
  currentChatId: '',
  persistentChatId: generateUUID(), // Generate initial persistent chat ID
  // Adaptive polling state
  pollAttemptsWithoutChange: 0,
  lastContentLength: 0,
  // PromptVault state
  promptVault: [],
  isLoadingPromptVault: false,
  promptVaultLoaded: false,
};

// Helper function to get initial sidebar state from localStorage
const getInitialSidebarState = (): boolean => {
  try {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : true; // Default to collapsed if not found
  } catch (error) {
    console.warn('Failed to load sidebar state from localStorage:', error);
    return true; // Default to collapsed on error
  }
};

const initialUIState: UIState = {
  selectedExpert: null,
  isLandingView: true,
  sidebarCollapsed: getInitialSidebarState(),
  rightSidebarCollapsed: true,
  attachedFiles: [],
  uploadedFileReferences: [],
  expertsPopupOpen: false,
  loadingMessage: '',
  errorMessage: '',
  showLoading: false,
  showError: false,
  showAllChatsView: false,
  sidebarLoading: false,
  operationStatus: '',
  fileUploadProgress: new Map(),
  completedFileUploads: new Set(),
  validationError: null,
  showValidationError: false,
};

type AppAction = 
  | { type: 'SET_EXPERTS'; payload: Expert[] }
  | { type: 'SET_FILTERED_EXPERTS'; payload: Expert[] }
  | { type: 'SET_SAVED_CHATS'; payload: Chat[] }
  | { type: 'SET_CURRENT_CHAT'; payload: Chat | null }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_LOADING_CHATS'; payload: boolean }
  | { type: 'ADD_MESSAGE'; payload: { chatId: string; message: Message } }
  | { type: 'UPDATE_MESSAGE'; payload: { chatId: string; messageId: string; content: string; isComplete?: boolean; images?: GeneratedImage[]; has_images?: boolean; response_id?: string; isGeneratingImages?: boolean; artifacts?: { name: string; url: string }[]; events?: UiStreamEvent[]; isReasoning?: boolean; displayStatus?: string } }
  | { type: 'REMOVE_MESSAGES_FROM_INDEX'; payload: { chatId: string; fromIndex: number } }
  | { type: 'SET_POLL_TIMEOUT'; payload: NodeJS.Timeout | null }
  | { type: 'SET_SHOULD_STOP_POLLING'; payload: boolean }
  | { type: 'SET_TYPEWRITER_INTERVAL'; payload: NodeJS.Timeout | null }
  | { type: 'SET_IS_TYPING'; payload: boolean }
  | { type: 'SET_FULL_CONTENT_BUFFER'; payload: string }
  | { type: 'SET_DISPLAYED_CONTENT'; payload: string }
  | { type: 'SET_CURRENT_EXPERT_MESSAGE_ID'; payload: string }
  | { type: 'SET_CURRENT_REQUEST_ID'; payload: string }
  | { type: 'SET_CURRENT_CHAT_ID'; payload: string }
  | { type: 'SET_PERSISTENT_CHAT_ID'; payload: string }
  | { type: 'RESET_POLL_ATTEMPTS' }
  | { type: 'INCREMENT_POLL_ATTEMPTS' }
  | { type: 'SET_LAST_CONTENT_LENGTH'; payload: number }
  | { type: 'SET_PROMPT_VAULT'; payload: PromptVaultItem[] }
  | { type: 'SET_LOADING_PROMPT_VAULT'; payload: boolean }
  | { type: 'INVALIDATE_PROMPT_VAULT_CACHE' };

type UIAction =
  | { type: 'SET_SELECTED_EXPERT'; payload: Expert | null }
  | { type: 'SET_IS_LANDING_VIEW'; payload: boolean }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'TOGGLE_RIGHT_SIDEBAR' }
  | { type: 'ADD_ATTACHED_FILE'; payload: File }
  | { type: 'REMOVE_ATTACHED_FILE'; payload: number }
  | { type: 'CLEAR_ATTACHED_FILES' }
  | { type: 'ADD_UPLOADED_FILE_REFERENCE'; payload: { file: File; key: string } }
  | { type: 'REMOVE_UPLOADED_FILE_REFERENCE'; payload: number }
  | { type: 'CLEAR_UPLOADED_FILE_REFERENCES' }
  | { type: 'SET_EXPERTS_POPUP_OPEN'; payload: boolean }
  | { type: 'SHOW_LOADING'; payload: string }
  | { type: 'HIDE_LOADING' }
  | { type: 'SHOW_ERROR'; payload: string }
  | { type: 'HIDE_ERROR' }
  | { type: 'SHOW_ALL_CHATS_VIEW' }
  | { type: 'HIDE_ALL_CHATS_VIEW' }
  | { type: 'SHOW_SIDEBAR_LOADING' }
  | { type: 'HIDE_SIDEBAR_LOADING' }
  | { type: 'SET_OPERATION_STATUS'; payload: '' | 'securing-transfer' | 'uploading-attachments' | 'thinking' }
  | { type: 'SET_FILE_UPLOAD_PROGRESS'; payload: { fileName: string; progress: number } }
  | { type: 'SET_FILE_UPLOAD_COMPLETE'; payload: string }
  | { type: 'CLEAR_FILE_UPLOAD_PROGRESS' }
  | { type: 'SET_VALIDATION_ERROR'; payload: string | null }
  | { type: 'SHOW_VALIDATION_ERROR' }
  | { type: 'HIDE_VALIDATION_ERROR' };

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_EXPERTS':
      return { ...state, experts: action.payload };
    case 'SET_FILTERED_EXPERTS':
      return { ...state, filteredExperts: action.payload };
    case 'SET_SAVED_CHATS':
      // console.log(`[appReducer] SET_SAVED_CHATS - Old chats: ${state.savedChats.length}, New chats: ${action.payload.length}`);
      // console.log(`[appReducer] SET_SAVED_CHATS - Old starred: ${state.savedChats.filter(c => c.isStarred).length}, New starred: ${action.payload.filter(c => c.isStarred).length}`);
      return { ...state, savedChats: action.payload };
    case 'SET_CURRENT_CHAT':
      return { ...state, currentChat: action.payload };
    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };
    case 'SET_LOADING_CHATS':
      return { ...state, isLoadingChats: action.payload };
    case 'ADD_MESSAGE':
      if (state.currentChat && state.currentChat.id === action.payload.chatId) {
        const updatedChat = {
          ...state.currentChat,
          messages: [...state.currentChat.messages, action.payload.message]
        };
        return { ...state, currentChat: updatedChat };
      }
      return state;
    case 'UPDATE_MESSAGE':
      if (state.currentChat && state.currentChat.id === action.payload.chatId) {
        // console.log('[UPDATE_MESSAGE] Updating message:', action.payload.messageId, 'isComplete:', action.payload.isComplete);
        const updatedMessages = state.currentChat.messages.map(msg => {
          if (msg.id === action.payload.messageId) {
            const updatedMsg = { 
              ...msg, 
              content: action.payload.content, 
              isStreaming: !action.payload.isComplete,
              images: action.payload.images || msg.images,
              has_images: action.payload.has_images || msg.has_images,
              isGeneratingImages: action.payload.isGeneratingImages !== undefined ? action.payload.isGeneratingImages : msg.isGeneratingImages,
              response_id: action.payload.response_id || msg.response_id,  // Store response ID for chaining
              artifacts: action.payload.artifacts || msg.artifacts,
              isReasoning: (action as any).payload?.isReasoning !== undefined ? (action as any).payload.isReasoning : (msg as any).isReasoning,
              displayStatus: action.payload.isComplete ? undefined : (action.payload.displayStatus ?? msg.displayStatus),
              events: ((): UiStreamEvent[] | undefined => {
                const existing = (msg as any).events as UiStreamEvent[] | undefined;
                const incoming = action.payload.events as UiStreamEvent[] | undefined;
                if (!incoming || incoming.length === 0) return existing;
                if (!existing || existing.length === 0) return incoming;
                // Merge by appending new ones that aren't duplicate by (type+timestamp)
                const key = (e: UiStreamEvent) => `${e.type}-${e.timestamp || ''}-${e.output_index ?? ''}-${e.content_index ?? ''}`;
                const seen = new Set(existing.map(key));
                const merged = existing.slice();
                for (const ev of incoming) {
                  const k = key(ev);
                  if (!seen.has(k)) {
                    merged.push(ev);
                    seen.add(k);
                  }
                }
                // Keep last 200
                return merged.length > 200 ? merged.slice(-200) : merged;
              })()
            } as any;
            // console.log('[UPDATE_MESSAGE] Message updated - images:', updatedMsg.images, 'has_images:', updatedMsg.has_images);
            return updatedMsg;
          }
          return msg;
        });
        const updatedChat = { ...state.currentChat, messages: updatedMessages };
        return { ...state, currentChat: updatedChat };
      }
      return state;
    case 'REMOVE_MESSAGES_FROM_INDEX':
      if (state.currentChat && state.currentChat.id === action.payload.chatId) {
        const updatedMessages = state.currentChat.messages.filter((_, index) => index < action.payload.fromIndex);
        const updatedChat = { ...state.currentChat, messages: updatedMessages };
        return { ...state, currentChat: updatedChat };
      }
      return state;
    case 'SET_POLL_TIMEOUT':
      return { ...state, currentPollTimeout: action.payload };
    case 'SET_SHOULD_STOP_POLLING':
      return { ...state, shouldStopPolling: action.payload };
    case 'SET_TYPEWRITER_INTERVAL':
      return { ...state, typewriterInterval: action.payload };
    case 'SET_IS_TYPING':
      return { ...state, isTyping: action.payload };
    case 'SET_FULL_CONTENT_BUFFER':
      return { ...state, fullContentBuffer: action.payload };
    case 'SET_DISPLAYED_CONTENT':
      return { ...state, displayedContent: action.payload };
    case 'SET_CURRENT_EXPERT_MESSAGE_ID':
      return { ...state, currentExpertMessageId: action.payload };
    case 'SET_CURRENT_REQUEST_ID':
      return { ...state, currentRequestId: action.payload };
    case 'SET_CURRENT_CHAT_ID':
      return { ...state, currentChatId: action.payload };
    case 'SET_PERSISTENT_CHAT_ID':
      return { ...state, persistentChatId: action.payload };
    case 'RESET_POLL_ATTEMPTS':
      return { ...state, pollAttemptsWithoutChange: 0 };
    case 'INCREMENT_POLL_ATTEMPTS':
      return { ...state, pollAttemptsWithoutChange: state.pollAttemptsWithoutChange + 1 };
    case 'SET_LAST_CONTENT_LENGTH':
      return { ...state, lastContentLength: action.payload };
    case 'SET_PROMPT_VAULT':
      return { ...state, promptVault: action.payload, promptVaultLoaded: true };
    case 'SET_LOADING_PROMPT_VAULT':
      return { ...state, isLoadingPromptVault: action.payload };
    case 'INVALIDATE_PROMPT_VAULT_CACHE':
      return { ...state, promptVaultLoaded: false };
    default:
      return state;
  }
};

const uiReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case 'SET_SELECTED_EXPERT':
      return { ...state, selectedExpert: action.payload };
    case 'SET_IS_LANDING_VIEW':
      return { ...state, isLandingView: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'TOGGLE_RIGHT_SIDEBAR':
      return { ...state, rightSidebarCollapsed: !state.rightSidebarCollapsed };
    case 'ADD_ATTACHED_FILE':
      return { ...state, attachedFiles: [...state.attachedFiles, action.payload] };
    case 'REMOVE_ATTACHED_FILE':
      return {
        ...state,
        attachedFiles: state.attachedFiles.filter((_, index) => index !== action.payload)
      };
    case 'CLEAR_ATTACHED_FILES':
      return { ...state, attachedFiles: [] };
    case 'ADD_UPLOADED_FILE_REFERENCE':
      return { ...state, uploadedFileReferences: [...state.uploadedFileReferences, action.payload] };
    case 'REMOVE_UPLOADED_FILE_REFERENCE':
      return {
        ...state,
        uploadedFileReferences: state.uploadedFileReferences.filter((_, index) => index !== action.payload)
      };
    case 'CLEAR_UPLOADED_FILE_REFERENCES':
      return { ...state, uploadedFileReferences: [] };
    case 'SET_EXPERTS_POPUP_OPEN':
      return { ...state, expertsPopupOpen: action.payload };
    case 'SHOW_LOADING':
      return { ...state, showLoading: true, loadingMessage: action.payload };
    case 'HIDE_LOADING':
      return { ...state, showLoading: false, loadingMessage: '' };
    case 'SHOW_ERROR':
      return { ...state, showError: true, errorMessage: action.payload };
    case 'HIDE_ERROR':
      return { ...state, showError: false, errorMessage: '' };
    case 'SHOW_ALL_CHATS_VIEW':
      return { ...state, showAllChatsView: true };
    case 'HIDE_ALL_CHATS_VIEW':
      return { ...state, showAllChatsView: false };
    case 'SHOW_SIDEBAR_LOADING':
      return { ...state, sidebarLoading: true };
    case 'HIDE_SIDEBAR_LOADING':
      return { ...state, sidebarLoading: false };
    case 'SET_OPERATION_STATUS':
      // console.log('UIReducer: Setting operationStatus from', state.operationStatus, 'to', action.payload);
      return { ...state, operationStatus: action.payload };
    case 'SET_FILE_UPLOAD_PROGRESS':
      const newProgressMap = new Map(state.fileUploadProgress);
      newProgressMap.set(action.payload.fileName, action.payload.progress);
      return { ...state, fileUploadProgress: newProgressMap };
    case 'SET_FILE_UPLOAD_COMPLETE':
      const newCompletedSet = new Set(state.completedFileUploads);
      newCompletedSet.add(action.payload);
      return { ...state, completedFileUploads: newCompletedSet };
    case 'CLEAR_FILE_UPLOAD_PROGRESS':
      return { ...state, fileUploadProgress: new Map(), completedFileUploads: new Set() };
    case 'SET_VALIDATION_ERROR':
      return { ...state, validationError: action.payload };
    case 'SHOW_VALIDATION_ERROR':
      return { ...state, showValidationError: true };
    case 'HIDE_VALIDATION_ERROR':
      return { ...state, showValidationError: false, validationError: null };
    default:
      return state;
  }
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [appState, appDispatch] = useReducer(appReducer, initialAppState);
  const [uiState, uiDispatch] = useReducer(uiReducer, initialUIState);
  const appStateRef = useRef(appState);
  const uiStateRef = useRef(uiState);
  const spacerUpdateCallback = useRef<(() => void) | null>(null);
  const lastSpacerUpdate = useRef<number>(0); // Throttling for spacer updates (optimized)

  // Blob URL cache to prevent excessive creation
  const blobUrlCache = useRef<Map<string, string>>(new Map());

  // Helper function to get or create blob URL for a file
  const getOrCreateBlobUrl = (file: File): string => {
    const key = `${file.name}-${file.size}-${file.lastModified}`;
    
    if (blobUrlCache.current.has(key)) {
      return blobUrlCache.current.get(key)!;
    }
    
    const blobUrl = URL.createObjectURL(file);
    blobUrlCache.current.set(key, blobUrl);
    return blobUrl;
  };

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      blobUrlCache.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlCache.current.clear();
    };
  }, []);

  // Instant file upload function
  const uploadFileInstantly = async (file: File): Promise<string | null> => {
    try {
      // Use the current chat ID if available, otherwise use the persistent chat ID
      const chatId = appState.currentChat?.id || appState.persistentChatId;
      
      // Set initial progress to 1% immediately so overlay shows
      uiDispatch({ type: 'SET_FILE_UPLOAD_PROGRESS', payload: { fileName: file.name, progress: 1 } });
      
      // Set status to securing transfer
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: 'securing-transfer' });
      
      // Get presigned URL
      const presignedData = await apiService.getPresignedUploadUrl(chatId, file);
      
      if (presignedData.uploadRequired && presignedData.url) {
        // Set status to uploading
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: 'uploading-attachments' });
        
        // Upload to S3 with progress tracking
        await apiService.uploadFileToS3(presignedData.url, file, file.type, (progress) => {
          // Ensure progress is at least 1% and at most 100%
          const normalizedProgress = Math.max(1, Math.min(100, progress));
          uiDispatch({ type: 'SET_FILE_UPLOAD_PROGRESS', payload: { fileName: file.name, progress: normalizedProgress } });
        });
        
        // Mark as complete
        uiDispatch({ type: 'SET_FILE_UPLOAD_COMPLETE', payload: file.name });
      } else {
        // No upload required, mark as complete immediately
        uiDispatch({ type: 'SET_FILE_UPLOAD_PROGRESS', payload: { fileName: file.name, progress: 100 } });
        uiDispatch({ type: 'SET_FILE_UPLOAD_COMPLETE', payload: file.name });
      }
      
      // Clear operation status if no other uploads are in progress
      const hasOtherUploads = Array.from(uiState.fileUploadProgress?.keys() || [])
        .some(fileName => fileName !== file.name && !uiState.completedFileUploads?.has(fileName));
      
      if (!hasOtherUploads) {
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
      }
      
      return presignedData.key;
    } catch (error) {
      console.error('Error uploading file instantly:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: `Failed to upload ${file.name}` });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
      return null;
    }
  };

  // Helper function to check if we need to generate a title
  const needsTitleGeneration = (chat: Chat | null): boolean => {
    // Only generate title if:
    // 1. No existing chat (new conversation), OR
    // 2. Existing chat has no messages (new conversation), OR  
    // 3. Existing chat still has placeholder title
    return !chat || 
           chat.messages.length === 0 || 
           chat.title === 'Untitled Chat';
  };

  // Helper function to find and set the selected expert based on the last expert message
  const setSelectedExpertFromLastMessage = (chat: Chat) => {
    if (!chat.messages || chat.messages.length === 0) {
      return;
    }

    // Find the last expert message to determine which expert was being used
    const lastExpertMessage = [...chat.messages]
      .reverse()
      .find(message => message.role === 'expert' && message.expertArchetype);
    
    if (lastExpertMessage && lastExpertMessage.expertArchetype) {
      // Find the expert that matches the name/archetype from the last message
      const matchingExpert = appState.experts.find(expert => 
        (expert.name && expert.name === lastExpertMessage.expertArchetype) ||
        (expert.archetype && expert.archetype === lastExpertMessage.expertArchetype) ||
        (expert.ARCHETYPE && expert.ARCHETYPE === lastExpertMessage.expertArchetype)
      );
      
      if (matchingExpert && matchingExpert !== uiState.selectedExpert) {
        // console.log(`[setSelectedExpertFromLastMessage] Setting selected expert to ${matchingExpert.archetype || matchingExpert.ARCHETYPE} based on last message`);
        uiDispatch({ type: 'SET_SELECTED_EXPERT', payload: matchingExpert });
        
        // Save selected expert to localStorage for consistency
        try {
          const expertId = matchingExpert.id || matchingExpert.ID || matchingExpert.name;
          if (expertId) {
            localStorage.setItem('selectedExpertId', expertId);
          }
        } catch (error) {
          console.warn('Failed to save selected expert to localStorage:', error);
        }
      }
    }
  };

  // Update ref whenever app state changes
  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  // Update ref whenever ui state changes
  useEffect(() => {
    uiStateRef.current = uiState;
  }, [uiState]);

  const loadExperts = async () => {
    try {
      const experts = await apiService.loadExperts();
      appDispatch({ type: 'SET_EXPERTS', payload: experts });
      appDispatch({ type: 'SET_FILTERED_EXPERTS', payload: experts });
      
      // Try to load previously selected expert from localStorage
      let selectedExpert: Expert | null = null;
      try {
        const savedExpertId = localStorage.getItem('selectedExpertId');
        if (savedExpertId) {
          selectedExpert = experts.find(e => 
            (e.id && e.id === savedExpertId) || 
            (e.ID && e.ID === savedExpertId) ||
            (e.name && e.name === savedExpertId)
          ) || null;
        }
      } catch (error) {
        console.warn('Failed to load saved expert from localStorage:', error);
      }
      
      // If no saved expert or saved expert not found, use default
      if (!selectedExpert) {
        // Try to find "General AI" or similar, fallback to first expert
        selectedExpert = experts.find(e => 
          (e.name && e.name.toLowerCase().includes('general')) ||
          (e.archetype && e.archetype.toLowerCase().includes('general')) ||
          (e.ARCHETYPE && e.ARCHETYPE.toLowerCase().includes('general'))
        ) || experts.find(e => 
          (e.name === 'General AI') ||
          (e.archetype === 'General AI') || 
          (e.ARCHETYPE === 'General AI')
        ) || experts[0];
      }
      
      if (selectedExpert) {
        uiDispatch({ type: 'SET_SELECTED_EXPERT', payload: selectedExpert });
      }
    } catch (error) {
      // console.error('Error loading experts:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to load experts' });
    }
  };

  const loadSavedChats = async () => {
    // console.log('[ChatProvider] loadSavedChats called');
    try {
      appDispatch({ type: 'SET_LOADING_CHATS', payload: true });
      const chats = await chatPersistenceService.loadChats();
      // console.log('[ChatProvider] loaded chats from API:', chats);
      const sortedChats = chatPersistenceService.sortChatsByTimestamp(chats);
      appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
      appDispatch({ type: 'SET_LOADING_CHATS', payload: false });
    } catch (error) {
      // console.error('Error loading chats:', error);
      appDispatch({ type: 'SET_LOADING_CHATS', payload: false });
    }
  };

  const loadSavedChatsWithSidebarLoading = async () => {
    // console.log('[ChatProvider] loadSavedChatsWithSidebarLoading called');
    try {
      uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' });
      const chats = await chatPersistenceService.loadChats();
      // console.log('[ChatProvider] loaded chats from API:', chats);
      const sortedChats = chatPersistenceService.sortChatsByTimestamp(chats);
      appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    } catch (error) {
      // console.error('Error loading chats:', error);
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    }
  };

  /**
   * Send a message. If `optimistic` is true, immediately add the user message and a placeholder expert message,
   * switch to chat view, and then call initializeChat and generateChatTitle asynchronously.
   * Only re-enable the submit button after the AI response is received.
   */
  const sendMessage = async (
    message: string,
    attachedFiles: File[] = [],
    optimistic: boolean = true
  ): Promise<{ requestId: string, chatId: string, expert: Expert } | undefined> => {
    if (!uiState.selectedExpert) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Please select an expert first' });
      return;
    }

    try {
      // Reset stop flag from any previous cancelled request so this new request can poll normally
      appStateRef.current.shouldStopPolling = false;
      appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
      appDispatch({ type: 'SET_PROCESSING', payload: true });
      let chat = appState.currentChat;
      let chatId: string;
      let userMessage: Message;
      let expertMessage: Message;
      const expert = uiState.selectedExpert;
      const now = new Date().toISOString();

      // Use pre-uploaded file references instead of uploading files during message sending
      const preUploadedFileReferences = uiState.uploadedFileReferences.map(ref => ({
        name: ref.file.name,
        type: ref.file.type,
        size: ref.file.size,
        key: ref.key
      }));

      // Determine all files to send: pre-uploaded files + previously attached files from this chat
      let allFilesToSend = [...attachedFiles]; // Start with currently attached files
      let allFileReferences = [...preUploadedFileReferences]; // Start with pre-uploaded file references
      
      // console.log(`[sendMessage] Starting with ${attachedFiles.length} newly attached files and ${preUploadedFileReferences.length} pre-uploaded files`);
      
      if (chat && chat.allAttachedFiles) {
        // console.log(`[sendMessage] Chat has ${chat.allAttachedFiles.length} previously attached files`);
        
        // Add previously attached files that aren't already in the current attachedFiles or file references
        chat.allAttachedFiles.forEach((existingFile: File) => {
          const fileAlreadyInSend = allFilesToSend.some(f => f.name === existingFile.name && f.size === existingFile.size);
          const fileAlreadyInReferences = allFileReferences.some(ref => ref.name === existingFile.name && ref.size === existingFile.size);
          
          if (!fileAlreadyInSend && !fileAlreadyInReferences) {
            // console.log(`[sendMessage] Including existing file: ${existingFile.name}`);
            allFilesToSend.push(existingFile);
            // Add as a file reference (these are assumed to be already uploaded from previous messages)
            allFileReferences.push({
              name: existingFile.name,
              type: existingFile.type,
              size: existingFile.size,
              key: `${existingFile.name}-${existingFile.size}` // Fallback key format
            });
          } else {
            // console.log(`[sendMessage] Skipping duplicate file: ${existingFile.name}`);
          }
        });
      }
      
      // console.log(`[sendMessage] Total files to send: ${allFilesToSend.length}, Total file references: ${allFileReferences.length}`);
      // allFilesToSend.forEach((file, index) => {
      //   console.log(`  File ${index}: ${file.name} (${file.size} bytes, type: ${file.type})`);
      // });
      
      // Show file cards if there are any files being sent (new or existing)
      let showFileCardsForThisMessage = allFilesToSend.length > 0;

      if (optimistic) {
        // 1. Create a temporary chat object if needed
        if (!chat) {
          chatId = appState.persistentChatId; // Use the persistent chat ID
          chat = {
            id: chatId,
            title: 'Untitled Chat',
            messages: [],
            timestamp: now,
            isStarred: false,
            expert: expert,
            allAttachedFiles: [...attachedFiles] // Initialize with currently attached files
          };
          appDispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
        } else {
          chatId = chat.id;
          
          // Update the chat's allAttachedFiles list with newly attached files
          if (attachedFiles.length > 0) {
            const updatedAllAttachedFiles = [...(chat.allAttachedFiles || [])];
            attachedFiles.forEach(newFile => {
              if (!updatedAllAttachedFiles.some(f => f.name === newFile.name && f.size === newFile.size)) {
                updatedAllAttachedFiles.push(newFile);
              }
            });
            
            const updatedChat: Chat = { ...chat, allAttachedFiles: updatedAllAttachedFiles };
            appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
            chat = updatedChat;
          }
        }
        
        // 2. Add user message - show file cards for all files being sent
        userMessage = {
          id: Date.now().toString() + '-user',
          content: message,
          role: 'user',
          timestamp: now,
          attachments: showFileCardsForThisMessage ? allFilesToSend.map(file => {
            const attachment: FileAttachment = {
              name: file.name,
              size: file.size,
              type: file.type,
              file
            };
            
            // For image files, create an object URL and store it for persistence
            if (file.type.startsWith('image/')) {
              attachment.url = getOrCreateBlobUrl(file);
            }
            
            return attachment;
          }) : [] // Don't show file cards if no files
        };
        appDispatch({ type: 'ADD_MESSAGE', payload: { chatId: chat.id, message: userMessage } });
        
        // 3. Add placeholder expert message (empty, isStreaming: true)
        expertMessage = {
          id: Date.now().toString() + '-expert',
          content: '',
          role: 'expert',
          expertArchetype: expert.name || expert.archetype,
          timestamp: now,
          isStreaming: true
          // requestId will be added after we get it from the API
        };
        appDispatch({ type: 'ADD_MESSAGE', payload: { chatId: chat.id, message: expertMessage } });
        
        // Track the expert message ID and chat ID immediately so stopResponsePolling
        // can find and update this message if the user cancels before polling starts
        appStateRef.current.currentExpertMessageId = expertMessage.id;
        appStateRef.current.currentChatId = chatId;
        appDispatch({ type: 'SET_CURRENT_EXPERT_MESSAGE_ID', payload: expertMessage.id });
        appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
        
        // 4. Switch to chat view if on landing
        if (uiState.isLandingView) {
          uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: false });
        }
        
        // Clear attached files from UI after they've been added to the message
        // Files are now persisted in chat.allAttachedFiles and will be included in subsequent messages
        if (attachedFiles.length > 0) {
          uiDispatch({ type: 'CLEAR_ATTACHED_FILES' });
        }
        
        // Clear pre-uploaded file references since they're now part of the message
        if (uiState.uploadedFileReferences.length > 0) {
          uiDispatch({ type: 'CLEAR_UPLOADED_FILE_REFERENCES' });
        }
        
        // 6. Send message and generate title in background
        (async () => {
          try {
            // Determine if we need to generate a title for this conversation
            const shouldGenerateTitle = needsTitleGeneration(chat);
            
            // Clear previous upload progress
            uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
            
            // Send message with pre-uploaded file references instead of uploading files
            const { requestId, fileReferences } = await apiService.sendMessageWithPreUploadedFiles(
              message, 
              expert, 
              allFileReferences, 
              chatId, 
              undefined, 
              (status) => uiDispatch({ type: 'SET_OPERATION_STATUS', payload: status })
            );
            
            // Update the expert message with the requestId for future status checking
            const updatedExpertMessage = { ...expertMessage, requestId };
            const updatedChatWithRequestId = {
              ...appStateRef.current.currentChat!,
              messages: appStateRef.current.currentChat!.messages.map(msg => 
                msg.id === expertMessage.id ? updatedExpertMessage : msg
              )
            };
            appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChatWithRequestId });
            
            // Save the chat with the requestId to ensure it's persisted (non-blocking)
            // console.log(`[ChatContext] Saving chat with requestId ${requestId} for message ${expertMessage.id}`);
            chatPersistenceService.saveChat(updatedChatWithRequestId).catch(error => {
              // console.error('Error saving chat after requestId update:', error);
            });
            
            // Update the user message attachments with S3 keys for persistence
            // console.log(`[ChatContext - optimistic] Received ${fileReferences.length} file references from server:`, fileReferences);
            // console.log(`[ChatContext - optimistic] showFileCardsForThisMessage: ${showFileCardsForThisMessage}`);
            
            if (fileReferences.length > 0 && showFileCardsForThisMessage) {
              // console.log(`[ChatContext - optimistic] Updating user message attachments with S3 keys`);
              const updatedUserMessage = {
                ...userMessage,
                attachments: fileReferences.map(fileRef => {
                  const originalFile = allFilesToSend.find(f => f.name === fileRef.name && f.size === fileRef.size);
                  const attachment: FileAttachment = {
                    name: fileRef.name,
                    size: fileRef.size,
                    type: fileRef.type,
                    key: fileRef.key,
                    file: originalFile // Keep the original file for immediate use
                  };
                  
                  // console.log(`[ChatContext - optimistic] Created attachment for ${fileRef.name}:`, {
                  //   name: attachment.name,
                  //   size: attachment.size,
                  //   type: attachment.type,
                  //   key: attachment.key,
                  //   hasFile: !!attachment.file,
                  //   hasUrl: !!attachment.url
                  // });
                  
                  // For image files, create an object URL and store it for persistence
                  if (originalFile && fileRef.type.startsWith('image/')) {
                    attachment.url = getOrCreateBlobUrl(originalFile);
                    // console.log(`[ChatContext - optimistic] Added URL for image ${fileRef.name}: ${attachment.url}`);
                  }
                  
                  return attachment;
                })
              };
              
              // console.log(`[ChatContext - optimistic] Updated user message with ${updatedUserMessage.attachments.length} attachments`);
              
              // Update the message in the current chat
              const updatedChat = {
                ...appStateRef.current.currentChat!,
                messages: appStateRef.current.currentChat!.messages.map(msg => 
                  msg.id === userMessage.id ? updatedUserMessage : msg
                )
              };
              appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
            } else {
              // console.log(`[ChatContext - optimistic] Not updating attachments - fileReferences: ${fileReferences.length}, showFileCards: ${showFileCardsForThisMessage}`);
            }
            
            // Check if user cancelled while the API call was in flight
            if (appStateRef.current.shouldStopPolling) {
              // console.log(`[ChatContext] User cancelled during API call for requestId: ${requestId}. Skipping polling.`);
              return;
            }
            
            // CRITICAL: Start polling immediately after getting requestId (don't wait for title)
            // This is wrapped in robust error handling to ensure polling ALWAYS starts
            // console.log(`[ChatContext] Starting polling for requestId: ${requestId}, chatId: ${chatId}`);
            
            // Retry logic to ensure polling starts even if there are transient failures
            let pollingStarted = false;
            let attempts = 0;
            const maxAttempts = 3;
            
            while (!pollingStarted && attempts < maxAttempts) {
              attempts++;
              try {
                // console.log(`[ChatContext] Polling attempt ${attempts}/${maxAttempts} for requestId: ${requestId}`);
                pollForResponse(requestId, chatId, expert);
                pollingStarted = true;
                // console.log(`[ChatContext] Polling successfully started on attempt ${attempts} for requestId: ${requestId}`);
              } catch (pollingError) {
                console.error(`[ChatContext] Polling attempt ${attempts} failed for requestId ${requestId}:`, pollingError);
                if (attempts === maxAttempts) {
                  // On final failure, force restart the polling by setting up the minimum state needed
                  console.error(`[ChatContext] All polling attempts failed for requestId ${requestId}. Force-starting with minimal state.`);
                  try {
                    // Reset polling state to ensure clean start
                    appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
                    appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: requestId });
                    appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
                    
                    // Start polling content directly - this is the core function that must work
                    pollForResponseContent(requestId, chatId, expert);
                    pollingStarted = true;
                    // console.log(`[ChatContext] Force-started polling for requestId: ${requestId}`);
                  } catch (forceError) {
                    console.error(`[ChatContext] CRITICAL: Even force-start failed for requestId ${requestId}:`, forceError);
                    // This should never happen, but if it does, show error and reset processing state
                    uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to start response polling. Please try again.' });
                    appDispatch({ type: 'SET_PROCESSING', payload: false });
                    uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
                    return;
                  }
                } else {
                  // Short delay before retry
                  await new Promise(resolve => setTimeout(resolve, 100));
                }
              }
            }
            
            // Generate title in parallel (don't block polling)
            if (shouldGenerateTitle) {
              apiService.generateChatTitle(message).then(generatedTitle => {
                // Update the chat title when ready
                if (generatedTitle) {
                  const updatedChat = {
                    ...appStateRef.current.currentChat!,
                    title: generatedTitle
                  };
                  appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
                  
                  // Update saved chats list immediately (optimistic update)
                  const existingChatIndex = appStateRef.current.savedChats.findIndex(c => c.id === chatId);
                  const updatedChats = existingChatIndex >= 0
                    ? appStateRef.current.savedChats.map((c, i) => i === existingChatIndex ? updatedChat : c)
                    : [...appStateRef.current.savedChats, updatedChat];
                  const sortedChats = chatPersistenceService.sortChatsByTimestamp(updatedChats);
                  appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
                  
                  // Save the chat with updated title to backend (non-blocking)
                  chatPersistenceService.saveChat(updatedChat).catch(error => {
                    // console.error('Error saving chat with updated title:', error);
                  });
                }
              }).catch(error => {
                // console.error('Error generating chat title:', error);
                // Title generation failed, but polling continues normally
              });
            }
            
            // Save the chat with the requestId (non-blocking)
            const chatToSave = appStateRef.current.currentChat!;
            // console.log(`[ChatContext] Final save - chat has ${chatToSave.messages.length} messages`);
            // chatToSave.messages.forEach((msg, idx) => {
            //   if (msg.role === 'expert') {
            //     console.log(`[ChatContext] Expert message ${idx}: id=${msg.id}, requestId="${msg.requestId}"`);
            //   }
            // });
            chatPersistenceService.saveChat(chatToSave).catch(error => {
              // console.error('Error saving chat after initialization:', error);
            });
            
            // Update saved chats list with initial chat
            const existingChatIndex = appStateRef.current.savedChats.findIndex(c => c.id === chatId);
            const updatedChats = existingChatIndex >= 0
              ? appStateRef.current.savedChats.map((c, i) => i === existingChatIndex ? appStateRef.current.currentChat! : c)
              : [...appStateRef.current.savedChats, appStateRef.current.currentChat!];
            const sortedChats = chatPersistenceService.sortChatsByTimestamp(updatedChats);
            appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
            
          } catch (error) {
            // console.error('Error sending message:', error);
            uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to send message. Please try again.' });
            appDispatch({ type: 'SET_PROCESSING', payload: false });
            uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
          }
        })();
        
        // 7. Return early (do not block on API)
        return;
      } else {
        // --- Original (non-optimistic) flow for follow-up messages ---
        if (!chat) {
          try {
            chatId = appState.persistentChatId; // Use the persistent chat ID
            
            // Always generate title for new conversations
            const generatedTitle = await apiService.generateChatTitle(message);
            
            chat = {
              id: chatId,
              title: generatedTitle,
              messages: [],
              timestamp: now,
              isStarred: false,
              expert: expert,
              allAttachedFiles: [...attachedFiles] // Initialize with currently attached files
            };
            appDispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
          } catch (error) {
            // console.error('Error generating chat title:', error);
            uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to initialize chat. Please try again.' });
            appDispatch({ type: 'SET_PROCESSING', payload: false });
            uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
            return;
          }
        } else {
          chatId = chat.id;
          
          // Update the chat's allAttachedFiles list with newly attached files
          if (attachedFiles.length > 0) {
            const updatedAllAttachedFiles = [...(chat.allAttachedFiles || [])];
            attachedFiles.forEach(newFile => {
              if (!updatedAllAttachedFiles.some(f => f.name === newFile.name && f.size === newFile.size)) {
                updatedAllAttachedFiles.push(newFile);
              }
            });
            
            const updatedChat: Chat = { ...chat, allAttachedFiles: updatedAllAttachedFiles };
            appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
            chat = updatedChat;
          }
        }
        
        // Add user message - show file cards for all files being sent
        userMessage = {
          id: Date.now().toString() + '-user',
          content: message,
          role: 'user',
          timestamp: now,
          attachments: showFileCardsForThisMessage ? allFilesToSend.map(file => {
            const attachment: FileAttachment = {
              name: file.name,
              size: file.size,
              type: file.type,
              file
            };
            
            // For image files, create an object URL and store it for persistence
            if (file.type.startsWith('image/')) {
              attachment.url = getOrCreateBlobUrl(file);
            }
            
            return attachment;
          }) : [] // Don't show file cards if no files
        };
        appDispatch({ type: 'ADD_MESSAGE', payload: { chatId: chat.id, message: userMessage } });
        
        // Clear previous upload progress
        uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
        
        // Send message with pre-uploaded file references instead of uploading files
        const { requestId, fileReferences } = await apiService.sendMessageWithPreUploadedFiles(
          message, 
          expert, 
          allFileReferences, 
          chatId, 
          undefined, 
          (status) => uiDispatch({ type: 'SET_OPERATION_STATUS', payload: status })
        );
        
        // Update the user message attachments with S3 keys for persistence
        // console.log(`[ChatContext] Received ${fileReferences.length} file references from server:`, fileReferences);
        // console.log(`[ChatContext] showFileCardsForThisMessage: ${showFileCardsForThisMessage}`);
        
        if (fileReferences.length > 0 && showFileCardsForThisMessage) {
          // console.log(`[ChatContext] Updating user message attachments with S3 keys`);
          const updatedUserMessage = {
            ...userMessage,
            attachments: fileReferences.map(fileRef => {
              const originalFile = allFilesToSend.find(f => f.name === fileRef.name && f.size === fileRef.size);
              const attachment: FileAttachment = {
                name: fileRef.name,
                size: fileRef.size,
                type: fileRef.type,
                key: fileRef.key,
                file: originalFile // Keep the original file for immediate use
              };
              
              // console.log(`[ChatContext] Created attachment for ${fileRef.name}:`, {
              //   name: attachment.name,
              //   size: attachment.size,
              //   type: attachment.type,
              //   key: attachment.key,
              //   hasFile: !!attachment.file,
              //   hasUrl: !!attachment.url
              // });
              
              // For image files, create an object URL and store it for persistence
              if (originalFile && fileRef.type.startsWith('image/')) {
                attachment.url = getOrCreateBlobUrl(originalFile);
                // console.log(`[ChatContext] Added URL for image ${fileRef.name}: ${attachment.url}`);
              }
              
              return attachment;
            })
          };
          
          // console.log(`[ChatContext] Updated user message with ${updatedUserMessage.attachments.length} attachments`);
          
          // Update the current chat with the updated message
          chat = {
            ...chat,
            messages: [...chat.messages.slice(0, -1), updatedUserMessage] // Replace the last message (userMessage)
          };
          appDispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
          userMessage = updatedUserMessage; // Update the reference for saving
        } else {
          // console.log(`[ChatContext] Not updating attachments - fileReferences: ${fileReferences.length}, showFileCards: ${showFileCardsForThisMessage}`);
        }
        
        // Check if user cancelled while the API call was in flight
        if (appStateRef.current.shouldStopPolling) {
          // console.log(`[ChatContext] User cancelled during API call for requestId: ${requestId}. Skipping polling.`);
          return;
        }
        
        // CRITICAL: Start polling with robust error handling to ensure it ALWAYS starts
        // console.log(`[ChatContext] Starting polling for requestId: ${requestId}, chatId: ${chatId}`);
        
        // Retry logic to ensure polling starts even if there are transient failures
        let pollingStarted = false;
        let attempts = 0;
        const maxAttempts = 3;
        
                 while (!pollingStarted && attempts < maxAttempts) {
           attempts++;
           try {
             // console.log(`[ChatContext] Polling attempt ${attempts}/${maxAttempts} for requestId: ${requestId}`);
             pollForResponse(requestId, chatId, expert);
             pollingStarted = true;
             // console.log(`[ChatContext] Polling successfully started on attempt ${attempts} for requestId: ${requestId}`);
           } catch (pollingError) {
            console.error(`[ChatContext] Polling attempt ${attempts} failed for requestId ${requestId}:`, pollingError);
            if (attempts === maxAttempts) {
              // On final failure, force restart the polling by setting up the minimum state needed
              console.error(`[ChatContext] All polling attempts failed for requestId ${requestId}. Force-starting with minimal state.`);
              try {
                // Reset polling state to ensure clean start
                appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
                appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: requestId });
                appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
                
                // Start polling content directly - this is the core function that must work
                pollForResponseContent(requestId, chatId, expert);
                pollingStarted = true;
                // console.log(`[ChatContext] Force-started polling for requestId: ${requestId}`);
              } catch (forceError) {
                console.error(`[ChatContext] CRITICAL: Even force-start failed for requestId ${requestId}:`, forceError);
                // This should never happen, but if it does, show error and reset processing state
                uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to start response polling. Please try again.' });
                appDispatch({ type: 'SET_PROCESSING', payload: false });
                uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
                return;
              }
            } else {
              // Short delay before retry
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
        }
        
        // Clear attached files from UI after they've been added to the message
        // Files are now persisted in chat.allAttachedFiles and will be included in subsequent messages
        if (attachedFiles.length > 0) {
          uiDispatch({ type: 'CLEAR_ATTACHED_FILES' });
        }
        
        // Clear pre-uploaded file references since they're now part of the message
        if (uiState.uploadedFileReferences.length > 0) {
          uiDispatch({ type: 'CLEAR_UPLOADED_FILE_REFERENCES' });
        }
        
        if (uiState.isLandingView) {
          uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: false });
        }
        // Save chat with user message (non-blocking)
        chatPersistenceService.saveChat({ ...chat, messages: [...chat.messages, userMessage] }).catch(error => {
          // console.error('Error saving chat with user message:', error);
        });
      }
    } catch (error) {
      // console.error('Error sending message:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to send message' });
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
      return;
    }
  };

  const pollForResponse = (requestId: string, chatId: string, expert: Expert) => {
    // Validate input parameters to prevent polling failures
    if (!requestId || !chatId || !expert) {
      console.error('[pollForResponse] Invalid parameters:', { requestId, chatId, expert });
      throw new Error('Invalid polling parameters: requestId, chatId, and expert are required');
    }
    
    // If the user already cancelled, don't start polling
    if (appStateRef.current.shouldStopPolling) {
      // console.log('[pollForResponse] Skipping - user already cancelled');
      return;
    }
    
    // console.log('[pollForResponse] Called with requestId:', requestId, 'chatId:', chatId, 'expert:', expert?.archetype);
    // console.log('[pollForResponse] Current shouldStopPolling state:', appStateRef.current.shouldStopPolling);
    
    try {
      // CRITICAL: Reset polling state to ensure clean start
      // Force update the ref immediately to prevent race conditions
      appStateRef.current.shouldStopPolling = false;
      appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
      appDispatch({ type: 'SET_IS_TYPING', payload: false });
      appDispatch({ type: 'SET_FULL_CONTENT_BUFFER', payload: '' });
      appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: '' });
      appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: requestId });
      appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
      appDispatch({ type: 'RESET_POLL_ATTEMPTS' });
      appDispatch({ type: 'SET_LAST_CONTENT_LENGTH', payload: 0 });
      
      // console.log('[pollForResponse] Reset shouldStopPolling to:', appStateRef.current.shouldStopPolling);
      
      // Only add initial expert message if there is not already a streaming expert message
      const currentChat = appStateRef.current.currentChat;
      // console.log('[pollForResponse] Current chat messages:', currentChat?.messages.map(m => ({ id: m.id, role: m.role, isStreaming: m.isStreaming, content: m.content.substring(0, 30) })));
      const streamingExpertMessage = currentChat && currentChat.messages.find(
        m => m.role === 'expert' && m.isStreaming
      );
      // console.log('[pollForResponse] Found streamingExpertMessage:', !!streamingExpertMessage);
      
      if (!streamingExpertMessage) {
        const expertMessage: Message = {
          id: Date.now().toString(),
          content: '',
          role: 'expert',
          expertArchetype: expert.name || expert.archetype,
          timestamp: new Date().toISOString(),
          isStreaming: true,
          requestId: requestId
        };
        // console.log('[pollForResponse] Adding new streaming expert message:', expertMessage.id);
        appDispatch({ type: 'ADD_MESSAGE', payload: { chatId, message: expertMessage } });
        appDispatch({ type: 'SET_CURRENT_EXPERT_MESSAGE_ID', payload: expertMessage.id });
      } else {
        // Use the existing streaming expert message, but update it with the requestId if not already set
        if (!streamingExpertMessage.requestId) {
          const updatedExpertMessage = { ...streamingExpertMessage, requestId };
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: { chatId, messageId: streamingExpertMessage.id, content: streamingExpertMessage.content, isComplete: false }
          });
          // Update the message with requestId in the current chat
          const updatedChat = {
            ...appStateRef.current.currentChat!,
            messages: appStateRef.current.currentChat!.messages.map(msg => 
              msg.id === streamingExpertMessage.id ? updatedExpertMessage : msg
            )
          };
          appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
        }
        appDispatch({ type: 'SET_CURRENT_EXPERT_MESSAGE_ID', payload: streamingExpertMessage.id });
      }
      
      // Start the polling loop with a small delay to ensure state updates have taken effect
      // console.log('[pollForResponse] Starting polling loop for requestId:', requestId);
      // console.log('[pollForResponse] Final shouldStopPolling check:', appStateRef.current.shouldStopPolling);
      
      // Use setTimeout to ensure all state updates have processed
      setTimeout(() => {
        // console.log('[pollForResponse] About to start pollForResponseContent, shouldStopPolling:', appStateRef.current.shouldStopPolling);
        pollForResponseContent(requestId, chatId, expert);
      }, 0);
      
      // console.log('[pollForResponse] Successfully initiated polling for requestId:', requestId);
      
    } catch (error) {
      console.error('[pollForResponse] Error setting up polling for requestId:', requestId, error);
      // Re-throw the error so the retry logic in sendMessage can handle it
      throw error;
    }
  };

  // Helper function to calculate adaptive polling interval
  const getAdaptivePollingInterval = (): number => {
    const attempts = appStateRef.current.pollAttemptsWithoutChange;
    // Start at 500ms, double every 2 attempts, max at 2000ms
    // 0-1 attempts: 500ms, 2-3 attempts: 1000ms, 4+ attempts: 2000ms
    if (attempts < 2) return 500;
    if (attempts < 4) return 1000;
    return 2000;
  };

  // Throttled spacer update function (optimized to max once per 100ms)
  const throttledSpacerUpdate = () => {
    const now = Date.now();
    if (now - lastSpacerUpdate.current >= 100) {
      lastSpacerUpdate.current = now;
      if (spacerUpdateCallback.current) {
        spacerUpdateCallback.current();
      }
    }
  };

  const pollForResponseContent = async (requestId: string, chatId: string, expert: Expert) => {
    // Check if we should stop polling
    if (appStateRef.current.shouldStopPolling) {
      // console.log('[pollForResponseContent] Stopping polling due to shouldStopPolling flag');
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
      return;
    }

    try {
      // console.log('[pollForResponseContent] About to call apiService.pollResponse with requestId:', requestId, 'chatId:', chatId);
      const { content, isComplete, images, has_images, response_id, error, hasError, artifacts, reasoning, events, displayStatus } = await apiService.pollResponse(requestId, chatId);
      // eslint-disable-next-line no-console
      console.debug('[ChatContext] pollForResponseContent:', { requestId, hasContent: !!content, isComplete, has_images, response_id, hasReasoning: typeof reasoning === 'string' && reasoning.length > 0 });
      
      // Re-check cancellation after await - user may have clicked Stop while the API call was in flight
      if (appStateRef.current.shouldStopPolling) {
        // console.log('[pollForResponseContent] Stopping after poll returned - user cancelled during API call');
        return;
      }
      
      // Check if we received an error response
      if (hasError && error) {
        console.error('[pollForResponseContent] Error response received:', error);
        appDispatch({
          type: 'UPDATE_MESSAGE',
          payload: { 
            chatId, 
            messageId: appStateRef.current.currentExpertMessageId, 
            content: error, 
            isComplete: true,
            isGeneratingImages: false
          }
        });
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        
        // Save chat after a short delay to ensure the UPDATE_MESSAGE dispatch has been processed
        setTimeout(() => {
          saveCurrentChat();
        }, 10);
        return;
      }
      
      // Surface streaming events immediately, even before text/images start
      if (Array.isArray(events) && events.length > 0) {
        appDispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            chatId,
            messageId: appStateRef.current.currentExpertMessageId,
            content: appStateRef.current.displayedContent || '',
            isComplete: false,
            events
          }
        });
        // If we were rendering transient reasoning text, clear it now so only avatar status shows activity
        if (!content && !(has_images && (images?.length || 0) > 0)) {
          appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: '' });
        }
      }

      // Get the current message to compare images
      const currentChat = appStateRef.current.currentChat;
      const currentMessage = currentChat?.messages.find(m => m.id === appStateRef.current.currentExpertMessageId);
      const currentImageCount = currentMessage?.images?.length || 0;
      const newImageCount = images?.length || 0;
      
      // Check if we have new images (even without text content changes)
      const hasNewImages = has_images && newImageCount > currentImageCount;
      
      // Check if we have new text content
      const hasNewContent = content && content !== appStateRef.current.fullContentBuffer;
      
      // Track content changes for adaptive polling
      const currentContentLength = content?.length || 0;
      const hasContentChanged = currentContentLength !== appStateRef.current.lastContentLength || hasNewImages;
      if (hasContentChanged) {
        appDispatch({ type: 'RESET_POLL_ATTEMPTS' });
        appDispatch({ type: 'SET_LAST_CONTENT_LENGTH', payload: currentContentLength });
      } else if (!isComplete) {
        appDispatch({ type: 'INCREMENT_POLL_ATTEMPTS' });
      }
      
      // Determine if we have reasoning to show (only when no real content yet)
      const shouldShowReasoning = (!hasNewContent 
        && !hasNewImages 
        && !isComplete 
        && typeof reasoning === 'string' 
        && reasoning.length > 0 
        && !(Array.isArray(events) && events.length > 0));
      
      if (hasNewContent || hasNewImages) {
        // New content or images received, start/update typewriter effect
        // console.log(`[pollForResponseContent] New content (${hasNewContent}) or images (${hasNewImages}) received for ${requestId}`);
        // If we were showing transient reasoning text, clear it to avoid prefix mismatch with typewriter
        const existingDisplayed = appStateRef.current.displayedContent || '';
        if (existingDisplayed.startsWith('[thinking]')) {
          // eslint-disable-next-line no-console
          console.debug('[ChatContext] Clearing transient reasoning before starting typewriter for', requestId);
          appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: '' });
        }
        
        // Improved image generation detection
        let isGeneratingImages = false;
        if (content) {
          // Detect if images might be generated based on content keywords
          const imageGenerationKeywords = ['image', 'diagram', 'chart', 'illustration', 'visual', 'picture', 'drawing', 'generate', 'create', 'show'];
          const mightGenerateImages = imageGenerationKeywords.some(keyword => 
            content.toLowerCase().includes(keyword)
          );
          
          // Set image generation flag if we detect potential image generation and response isn't complete
          isGeneratingImages = mightGenerateImages && !isComplete;
        }
        
        // If we only have new images (no text), update the message directly
        if (hasNewImages && !hasNewContent) {
          // console.log(`[pollForResponseContent] Updating message with new images only for ${requestId}`);
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: { 
              chatId, 
              messageId: appStateRef.current.currentExpertMessageId, 
              content: appStateRef.current.displayedContent || '', 
              isComplete: false,
              images,
              has_images,
              response_id,
              isGeneratingImages: !isComplete,  // Only show as generating when response isn't complete
              artifacts,
              events
            }
          });
          
          // Continue polling with adaptive interval
          if (!isComplete) {
            const interval = getAdaptivePollingInterval();
            const timeout = setTimeout(() => pollForResponseContent(requestId, chatId, expert), interval);
            appDispatch({ type: 'SET_POLL_TIMEOUT', payload: timeout });
          } else {
            // Response is complete, ensure isGeneratingImages is false
            appDispatch({
              type: 'UPDATE_MESSAGE',
              payload: { 
                chatId, 
                messageId: appStateRef.current.currentExpertMessageId, 
                content: appStateRef.current.displayedContent || '', 
                isComplete: true,
                images,
                has_images,
                response_id,
                isGeneratingImages: false,
                artifacts
              }
            });
            // Clear processing state and operation status so UI shows submit button and hides "Thinking..."
            appDispatch({ type: 'SET_PROCESSING', payload: false });
            uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
            setTimeout(() => {
              saveCurrentChat();
            }, 10);
          }
        } else {
          // Start typewriter effect for text content (will also include images)
          // For text updates, also push any incoming event telemetry to the message so user sees phases
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              chatId,
              messageId: appStateRef.current.currentExpertMessageId,
              content: appStateRef.current.displayedContent || content || '',
              isComplete: false,
              images,
              has_images,
              response_id,
              isGeneratingImages,
              artifacts,
              events
            }
          });
          startTypewriterEffect(content || appStateRef.current.fullContentBuffer, isComplete, requestId, chatId, expert, images, has_images, response_id, isGeneratingImages, artifacts);
        }
      } else if (isComplete && !appStateRef.current.isTyping) {
        // Response complete but no new content and not currently typing
        // console.log(`[pollForResponseContent] Response complete for ${requestId}, finalizing message`);
        appDispatch({
          type: 'UPDATE_MESSAGE',
          payload: { chatId, messageId: appStateRef.current.currentExpertMessageId, content: appStateRef.current.displayedContent, isComplete: true, images, has_images, response_id, isGeneratingImages: false, artifacts, events }
        });
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        
        // Save chat after a short delay to ensure the UPDATE_MESSAGE dispatch has been processed
        setTimeout(() => {
          saveCurrentChat();
        }, 10);
      } else if (!isComplete && !appStateRef.current.isTyping) {
        // No new assistant content yet. Surface async display status or transient reasoning.
        if (displayStatus) {
          // Async expert interim status (e.g., "Extracting — reading documents...")
          // Pass displayStatus to the message so the renderer shows it below the spinner
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: { 
              chatId, 
              messageId: appStateRef.current.currentExpertMessageId, 
              content: '', 
              isComplete: false,
              images,
              has_images,
              response_id,
              isGeneratingImages: false,
              artifacts,
              displayStatus
            }
          });
        } else if (shouldShowReasoning) {
          // eslint-disable-next-line no-console
          console.debug('[ChatContext] Displaying transient reasoning for', requestId, 'len=', reasoning?.length || 0);
          const currentMessageId = appStateRef.current.currentExpertMessageId;
          // Ensure each reasoning chunk is rendered on its own line
          const normalized = reasoning.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          const display = `${normalized}`;
          appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: display });
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: { 
              chatId, 
              messageId: currentMessageId, 
              content: display, 
              isComplete: false,
              images,
              has_images,
              response_id,
              isGeneratingImages: false,
              artifacts
            }
          });
        }
        // Continue polling with adaptive interval
        const interval = getAdaptivePollingInterval();
        const timeout = setTimeout(() => pollForResponseContent(requestId, chatId, expert), interval);
        appDispatch({ type: 'SET_POLL_TIMEOUT', payload: timeout });
      } else {
        // console.log(`[pollForResponseContent] Waiting for typewriter to finish for ${requestId}`);
      }
      // If isTyping is true, the typewriter effect will handle the next poll
    } catch (error) {
      console.error('[pollForResponseContent] Error polling response for requestId:', requestId, error);
      
      // Check if this is a transient error that we should retry
      const isRetryableError = error instanceof TypeError || 
                              (error as any)?.message?.includes('fetch') ||
                              (error as any)?.message?.includes('network') ||
                              (error as any)?.message?.includes('timeout');
      
      if (isRetryableError && !appStateRef.current.shouldStopPolling) {
        // Retry after a short delay for transient errors
        // console.log(`[pollForResponseContent] Retrying polling for ${requestId} after transient error`);
        const timeout = setTimeout(() => pollForResponseContent(requestId, chatId, expert), 2000);
        appDispatch({ type: 'SET_POLL_TIMEOUT', payload: timeout });
      } else {
        // Final error, stop polling
        console.error(`[pollForResponseContent] Final error for ${requestId}, stopping polling`);
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to get response from AI. Please try again.' });
      }
    }
  };

  const startTypewriterEffect = (newContent: string, isComplete: boolean, requestId: string, chatId: string, expert: Expert, images?: GeneratedImage[], has_images?: boolean, response_id?: string, isGeneratingImages?: boolean, artifacts?: { name: string; url: string }[]) => {
    if (appStateRef.current.isTyping) {
      // Update buffer but don't start new typewriter until current one finishes
      appDispatch({ type: 'SET_FULL_CONTENT_BUFFER', payload: newContent });
      return;
    }

    appDispatch({ type: 'SET_FULL_CONTENT_BUFFER', payload: newContent });
    appDispatch({ type: 'SET_IS_TYPING', payload: true });

    // If the response is complete, skip typewriter effect and show full content immediately
    if (isComplete) {
      const currentMessageId = appStateRef.current.currentExpertMessageId;
      
      // Set displayed content to the full content
      appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: newContent });
      
      // Update the message with full content and mark as complete
      appDispatch({
        type: 'UPDATE_MESSAGE',
        payload: { 
          chatId, 
          messageId: currentMessageId, 
          content: newContent, 
          isComplete: true,
          images,
          has_images,
          response_id,
          isGeneratingImages: false,  // Always false when complete
          artifacts
        }
      });

      // Mark as not typing and not processing FIRST
      appDispatch({ type: 'SET_IS_TYPING', payload: false });
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });

      // Single immediate spacer update for final content - no staggered delays to prevent text selection interference
      if (spacerUpdateCallback.current) {
        spacerUpdateCallback.current();
      }
      
      // Save chat after a short delay to ensure the UPDATE_MESSAGE dispatch has been processed
      setTimeout(() => {
        saveCurrentChat();
      }, 10);
      
      return;
    }

    const typewriterStep = () => {
      // Use ref to get fresh state values
      const freshState = appStateRef.current;
      
      // Check current state for stop condition
      if (freshState.shouldStopPolling) {
        if (freshState.typewriterInterval) {
          clearInterval(freshState.typewriterInterval);
          appDispatch({ type: 'SET_TYPEWRITER_INTERVAL', payload: null });
        }
        appDispatch({ type: 'SET_IS_TYPING', payload: false });
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        return;
      }

      // Get fresh state values each time
      const currentBuffer = freshState.fullContentBuffer;
      const currentDisplayed = freshState.displayedContent;
      const currentMessageId = freshState.currentExpertMessageId;

      if (currentDisplayed.length < currentBuffer.length) {
        // Add twenty characters per tick (optimized from 10 for better performance)
        const newDisplayed = currentBuffer.substring(0, currentDisplayed.length + 20);
        appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: newDisplayed });
        
        appDispatch({
          type: 'UPDATE_MESSAGE',
          payload: { 
            chatId, 
            messageId: currentMessageId, 
            content: newDisplayed, 
            isComplete: false,
            images,
            has_images,
            response_id,
            isGeneratingImages,
            artifacts
          }
        });
        
        // Update spacer on every character during active typewriting only (throttled to max once per 100ms)
        if (appStateRef.current.isTyping && appStateRef.current.isProcessing) {
          if (images && images.length > 0) {
            // For messages with images, use longer delay to prevent flickering
            setTimeout(() => {
              // Double-check we're still processing to prevent interference after completion
              if (appStateRef.current.isTyping && appStateRef.current.isProcessing) {
                throttledSpacerUpdate();
              }
            }, 75);
          } else {
            // For text-only messages, throttled immediate update
            throttledSpacerUpdate();
          }
        }
      } else {
        // Typewriter effect complete
        if (freshState.typewriterInterval) {
          clearInterval(freshState.typewriterInterval);
          appDispatch({ type: 'SET_TYPEWRITER_INTERVAL', payload: null });
        }
        appDispatch({ type: 'SET_IS_TYPING', payload: false });

        // Continue polling for more content with adaptive interval - use the CONTENT polling function, not the main one
        const interval = getAdaptivePollingInterval();
        const timeout = setTimeout(() => {
          pollForResponseContent(requestId, chatId, expert);
        }, interval);
        appDispatch({ type: 'SET_POLL_TIMEOUT', payload: timeout });
      }
    };

    // Start the typewriter effect (30ms interval optimized from 1ms for better performance)
    const interval = setInterval(typewriterStep, 30);
    appDispatch({ type: 'SET_TYPEWRITER_INTERVAL', payload: interval });
  };

  const saveCurrentChat = async () => {
    // Use the ref to get the most current state
    const updatedChat = appStateRef.current.currentChat;
    if (updatedChat) {
      // Update local state immediately (optimistic update)
      const existingChatIndex = appStateRef.current.savedChats.findIndex(c => c.id === updatedChat.id);
      
      if (existingChatIndex >= 0) {
        const updatedChats = [...appStateRef.current.savedChats];
        updatedChats[existingChatIndex] = updatedChat;
        const sortedChats = chatPersistenceService.sortChatsByTimestamp(updatedChats);
        appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
      } else {
        const newChats = [...appStateRef.current.savedChats, updatedChat];
        const sortedChats = chatPersistenceService.sortChatsByTimestamp(newChats);
        appDispatch({ type: 'SET_SAVED_CHATS', payload: sortedChats });
      }
      
      // Save to backend asynchronously (non-blocking)
      chatPersistenceService.saveChat(updatedChat).catch(error => {
        // console.error('Error saving chat:', error);
      });
    }
  };

  const stopResponsePolling = () => {
    // console.log('[stopResponsePolling] Called');
    // console.log('[stopResponsePolling] currentExpertMessageId:', appStateRef.current.currentExpertMessageId);
    // console.log('[stopResponsePolling] currentChatId:', appStateRef.current.currentChatId);
    // console.log('[stopResponsePolling] displayedContent:', appStateRef.current.displayedContent);
    
    // Log current messages to debug
    // if (appStateRef.current.currentChat) {
    //   console.log('[stopResponsePolling] Current chat messages:');
    //   appStateRef.current.currentChat.messages.forEach((msg, idx) => {
    //     console.log(`  ${idx}: id=${msg.id}, role=${msg.role}, isStreaming=${msg.isStreaming}, content="${msg.content.substring(0, 30)}..."`);
    //   });
    // }
    
    // CRITICAL: Directly mutate the ref for immediate visibility to running timers/callbacks.
    // Dispatches are async (processed after React re-render + useEffect), so without this,
    // in-flight polls and typewriter steps won't see the stop flag until the next render cycle.
    appStateRef.current.shouldStopPolling = true;
    appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: true });
    if (appStateRef.current.currentPollTimeout) {
      clearTimeout(appStateRef.current.currentPollTimeout);
      appStateRef.current.currentPollTimeout = null;
      appDispatch({ type: 'SET_POLL_TIMEOUT', payload: null });
    }
    if (appStateRef.current.typewriterInterval) {
      clearInterval(appStateRef.current.typewriterInterval);
      appStateRef.current.typewriterInterval = null;
      appDispatch({ type: 'SET_TYPEWRITER_INTERVAL', payload: null });
    }
    appStateRef.current.isTyping = false;
    appStateRef.current.isProcessing = false;
    appDispatch({ type: 'SET_IS_TYPING', payload: false });
    appDispatch({ type: 'SET_PROCESSING', payload: false });
    uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
    
    // Update the current expert message to mark it as complete (stops streaming and changes avatar)
    if (appStateRef.current.currentExpertMessageId && appStateRef.current.currentChatId) {
      // console.log('[stopResponsePolling] Updating message to mark as complete');
      appDispatch({
        type: 'UPDATE_MESSAGE',
        payload: { 
          chatId: appStateRef.current.currentChatId,
          messageId: appStateRef.current.currentExpertMessageId,
          content: appStateRef.current.displayedContent || '',
          isComplete: true
        }
      });
    }
    
    // Fallback: find and mark ALL streaming expert messages in the current chat as complete.
    // This handles cases where the user cancels before currentExpertMessageId was set
    // (e.g., during the initial API call in the optimistic sendMessage flow).
    const currentChat = appStateRef.current.currentChat;
    if (currentChat) {
      const streamingMessages = currentChat.messages.filter(m => m.role === 'expert' && m.isStreaming);
      for (const msg of streamingMessages) {
        if (msg.id !== appStateRef.current.currentExpertMessageId) {
          appDispatch({
            type: 'UPDATE_MESSAGE',
            payload: {
              chatId: currentChat.id,
              messageId: msg.id,
              content: msg.content || '',
              isComplete: true
            }
          });
        }
      }
    }
    
    appDispatch({ type: 'SET_FULL_CONTENT_BUFFER', payload: '' });
    appDispatch({ type: 'SET_DISPLAYED_CONTENT', payload: '' });
    appDispatch({ type: 'SET_CURRENT_EXPERT_MESSAGE_ID', payload: '' });
    appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: '' });
    appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: '' });
    
    // console.log('[stopResponsePolling] Completed');
  };

  const loadChat = async (chatId: string) => {
    try {
      // Stop any active response polling first when switching between chats
      if (appState.isProcessing || appState.currentExpertMessageId) {
        // console.log('[loadChat] Stopping active response polling before loading new chat');
        stopResponsePolling();
        
        // Save the current chat with finalized message
        if (appStateRef.current.currentChat) {
          await saveCurrentChat();
        }
      }
      
      uiDispatch({ type: 'SHOW_LOADING', payload: 'Loading chat...' });
      
      // Fetch the full chat content from the API
      const fullChat = await chatPersistenceService.getChatById(chatId);
      
      if (fullChat) {
        // Pre-load all file attachments from the server
        // This will fetch all files immediately when the chat loads for instant access
        // console.log(`[loadChat] Pre-loading all file attachments for chat ${chatId}`);
        
        const fileCache = new Map<string, File>(); // Cache files by name to avoid duplicates
        const processedFiles = new Set<string>(); // Track by name+size to avoid duplicates
        
        // First pass: collect all unique file attachments
        const filesToFetch: Array<{name: string, size: number, type: string, key: string}> = [];
        
        for (const message of fullChat.messages) {
          if (message.attachments) {
            for (const attachment of message.attachments) {
              const fileKey = `${attachment.name}-${attachment.size}`;
              
              // Only process attachments with S3 keys that we haven't already processed
              if (attachment.key && !processedFiles.has(fileKey)) {
                filesToFetch.push({
                  name: attachment.name,
                  size: attachment.size,
                  type: attachment.type,
                  key: attachment.key
                });
                processedFiles.add(fileKey);
              }
            }
          }
        }
        
        // console.log(`[loadChat] Found ${filesToFetch.length} unique files to fetch`);
        
        // Second pass: fetch all files in parallel
        const filePromises = filesToFetch.map(async (fileInfo) => {
          try {
            // console.log(`[loadChat] Fetching file: ${fileInfo.name}`);
            
            const url = `${API_BASE_URL}/api/chat/${chatId}/files/${encodeURIComponent(fileInfo.name)}`;
            const response = await fetch(
              url,
              createAuthenticatedRequestOptions({
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                }
              })
            );
            
            if (response.ok) {
              const data = await response.json();
              
              // Convert base64 content back to a File object
              const base64Data = data.content;
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: data.contentType });
              const file = new File([blob], fileInfo.name, { type: data.contentType });
              
              // console.log(`[loadChat] Successfully fetched file: ${fileInfo.name} (${file.size} bytes)`);
              fileCache.set(fileInfo.name, file);
              
              return { name: fileInfo.name, file };
            } else {
              console.error(`[loadChat] Failed to fetch file ${fileInfo.name}: ${response.status}`);
              return { name: fileInfo.name, file: null };
            }
          } catch (error) {
            console.error(`[loadChat] Error fetching file ${fileInfo.name}:`, error);
            return { name: fileInfo.name, file: null };
          }
        });
        
        // Wait for all files to be fetched
        const fetchResults = await Promise.all(filePromises);
        
        // Third pass: update all message attachments with the actual File objects
        const updatedMessages = fullChat.messages.map(message => {
          if (message.attachments && message.attachments.length > 0) {
            const updatedAttachments = message.attachments.map(attachment => {
              const cachedFile = fileCache.get(attachment.name);
              if (cachedFile) {
                // console.log(`[loadChat] Updating attachment ${attachment.name} with actual File object`);
                return {
                  ...attachment,
                  file: cachedFile
                };
              }
              return attachment;
            });
            
            return {
              ...message,
              attachments: updatedAttachments
            };
          }
          return message;
        });
        
        // Fourth pass: build allAttachedFiles from successfully fetched files
        const allAttachedFiles = Array.from(fileCache.values());
        
        // Check for incomplete messages and try to get updated content from backend
        let hasUpdatedMessages = false;
        let shouldResumePolling = false;
        let pollingInfo: { requestId: string; chatId: string; expert: Expert; messageId: string } | null = null;
        
        const messagesWithUpdatedContent = await Promise.all(
          updatedMessages.map(async (message) => {
            if (message.role === 'expert' && message.isStreaming) {
              // console.log(`[loadChat] Checking for updated content for incomplete message: ${message.id}`);
              
              try {
                // Check the backend for updated content using the requestId if available
                // console.log(`[loadChat] Message ${message.id} has requestId: "${message.requestId}"`);
                const statusResult = await apiService.checkMessageStatus(chatId, message.requestId);
                
                if (statusResult && statusResult.content && statusResult.content.length > message.content.length) {
                  // console.log(`[loadChat] Found updated content for message ${message.id}. Old length: ${message.content.length}, New length: ${statusResult.content.length}`);
                  hasUpdatedMessages = true;
                  
                  // If response is still not complete, we should resume polling
                  if (!statusResult.isComplete && message.requestId && message.requestId.trim() !== '') {
                    // console.log(`[loadChat] Response still in progress for message ${message.id}, will resume polling with requestId: ${message.requestId}`);
                    shouldResumePolling = true;
                    pollingInfo = { 
                      requestId: message.requestId, 
                      chatId: chatId, 
                      expert: fullChat.expert || uiState.selectedExpert!,
                      messageId: message.id
                    };
                  }
                  
                  return {
                    ...message,
                    content: statusResult.content,
                    isStreaming: !statusResult.isComplete
                  };
                } else if (statusResult && statusResult.isComplete && message.isStreaming) {
                  // console.log(`[loadChat] Message ${message.id} is now complete, marking as non-streaming`);
                  hasUpdatedMessages = true;
                  return {
                    ...message,
                    isStreaming: false
                  };
                } else if (statusResult && !statusResult.isComplete && message.requestId && message.requestId.trim() !== '') {
                  // Content hasn't changed but response is still in progress
                  // console.log(`[loadChat] Response still in progress for message ${message.id} with no new content yet, will resume polling with requestId: ${message.requestId}`);
                  shouldResumePolling = true;
                  pollingInfo = { 
                    requestId: message.requestId, 
                    chatId: chatId, 
                    expert: fullChat.expert || uiState.selectedExpert!,
                    messageId: message.id
                  };
                  return message; // Keep as-is, polling will update it
                } else {
                  // console.log(`[loadChat] No updates found for message ${message.id}, marking as complete`);
                  // No response found or no new content, assume it's complete
                  return {
                    ...message,
                    isStreaming: false
                  };
                }
              } catch (error) {
                console.error(`[loadChat] Error checking status for message ${message.id}:`, error);
                // On error, mark as complete to avoid infinite streaming state
                return {
                  ...message,
                  isStreaming: false
                };
              }
            }
            return message;
          })
        );
        
        // Update the chat with pre-loaded files and updated messages
        const chatWithPreloadedFiles = {
          ...fullChat,
          messages: messagesWithUpdatedContent,
          allAttachedFiles: allAttachedFiles
        };
        
        // If we updated any messages, save the updated chat
        if (hasUpdatedMessages) {
          // console.log(`[loadChat] Saving chat with ${hasUpdatedMessages ? 'updated' : 'cleaned'} messages`);
          chatPersistenceService.saveChat(chatWithPreloadedFiles).catch(error => {
            console.error('Error saving updated chat:', error);
          });
        }
        
        // console.log(`[loadChat] Pre-loaded ${allAttachedFiles.length} files for chat ${chatId}`);
        fetchResults.forEach(result => {
          if (result.file) {
            // console.log(`  ✓ ${result.name} - loaded successfully`);
          } else {
            // console.log(`  ✗ ${result.name} - failed to load`);
          }
        });
        
        // Set the selected expert based on the last expert message
        setSelectedExpertFromLastMessage(chatWithPreloadedFiles);

        appDispatch({ type: 'SET_CURRENT_CHAT', payload: chatWithPreloadedFiles });
        uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: false });
        
        // Resume polling if we found a response still in progress
        if (shouldResumePolling && pollingInfo) {
          // console.log(`[loadChat] Resuming polling for request ${pollingInfo.requestId} on message ${pollingInfo.messageId}`);
          
          // Set up polling state
          appDispatch({ type: 'SET_PROCESSING', payload: true });
          appDispatch({ type: 'SET_CURRENT_EXPERT_MESSAGE_ID', payload: pollingInfo.messageId });
          appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: pollingInfo.requestId });
          appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: pollingInfo.chatId });
          appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
          
          // CRITICAL: Force update the ref immediately to prevent race condition
          appStateRef.current.shouldStopPolling = false;
          appStateRef.current.currentRequestId = pollingInfo.requestId;
          appStateRef.current.currentChatId = pollingInfo.chatId;
          appStateRef.current.currentExpertMessageId = pollingInfo.messageId;
          appStateRef.current.isProcessing = true;
          
          // console.log(`[loadChat] Force-updated appStateRef.current.shouldStopPolling to: ${appStateRef.current.shouldStopPolling}`);
          
          // Add a small delay to ensure all state updates have processed
          setTimeout(() => {
            // console.log(`[loadChat] About to call pollForResponseContent - shouldStopPolling: ${appStateRef.current.shouldStopPolling}`);
            // console.log(`[loadChat] Full appStateRef.current polling state:`, {
            //   shouldStopPolling: appStateRef.current.shouldStopPolling,
            //   currentRequestId: appStateRef.current.currentRequestId,
            //   currentChatId: appStateRef.current.currentChatId,
            //   currentExpertMessageId: appStateRef.current.currentExpertMessageId,
            //   isProcessing: appStateRef.current.isProcessing
            // });
            
            // Start polling immediately
            try {
              pollForResponseContent(pollingInfo.requestId, pollingInfo.chatId, pollingInfo.expert);
              // console.log(`[loadChat] Successfully resumed polling for request ${pollingInfo.requestId}`);
            } catch (error) {
              console.error(`[loadChat] Failed to resume polling for request ${pollingInfo.requestId}:`, error);
              // Reset processing state on error
              appDispatch({ type: 'SET_PROCESSING', payload: false });
              uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
            }
          }, 100);
        } else {
          // console.log(`[loadChat] Not resuming polling - shouldResumePolling: ${shouldResumePolling}, pollingInfo:`, pollingInfo);
        }
      } else {
        // If API call fails, fallback to local metadata (will show empty chat)
        const chat = appState.savedChats.find(c => c.id === chatId);
        if (chat) {
          // Set the selected expert based on the last expert message
          setSelectedExpertFromLastMessage(chat);

          appDispatch({ type: 'SET_CURRENT_CHAT', payload: chat });
          uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: false });
        } else {
          uiDispatch({ type: 'SHOW_ERROR', payload: 'Chat not found' });
        }
      }
    } catch (error) {
      // console.error('Error loading chat:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to load chat' });
    } finally {
      uiDispatch({ type: 'HIDE_LOADING' });
    }
  };

  const deleteChat = async (chatId: string) => {
    uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' });
    try {
      await chatPersistenceService.deleteChat(chatId);
      
      // Update saved chats in state directly instead of calling loadSavedChats()
      const updatedChats = appState.savedChats.filter(chat => chat.id !== chatId);
      appDispatch({ type: 'SET_SAVED_CHATS', payload: updatedChats });
      
      if (appState.currentChat?.id === chatId) {
        appDispatch({ type: 'SET_CURRENT_CHAT', payload: null });
        uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: true });
      }
    } catch (error) {
      // console.error('Error deleting chat:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to delete chat' });
    } finally {
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    }
  };

  const deleteMultipleChats = async (chatIds: string[]) => {
    uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' });
    try {
      const result = await chatPersistenceService.bulkDeleteChats(chatIds);
      
      // Update saved chats in state directly instead of calling loadSavedChats()
      const updatedChats = appState.savedChats.filter(chat => !chatIds.includes(chat.id));
      appDispatch({ type: 'SET_SAVED_CHATS', payload: updatedChats });
      
      if (appState.currentChat && chatIds.includes(appState.currentChat.id)) {
        appDispatch({ type: 'SET_CURRENT_CHAT', payload: null });
        uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: true });
      }
      
      // Show success/partial success message
      if (result.failedDeletes && result.failedDeletes.length > 0) {
        const successCount = result.deletedCount;
        const failedCount = result.failedDeletes.length;
        uiDispatch({ type: 'SHOW_ERROR', payload: `Deleted ${successCount} chats successfully, but ${failedCount} failed to delete.` });
      }
    } catch (error) {
      // console.error('Error deleting chats:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to delete chats' });
    } finally {
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    }
  };

  const renameChat = async (chatId: string, newTitle: string) => {
    uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' });
    try {
      // Load the chat and update the title
      const chat = await chatPersistenceService.getChatById(chatId);
      if (chat) {
        chat.title = newTitle;
        
        // Update saved chats in state immediately (optimistic update)
        const updatedChats = appState.savedChats.map(c => 
          c.id === chatId ? { ...c, title: newTitle } : c
        );
        appDispatch({ type: 'SET_SAVED_CHATS', payload: updatedChats });
        
        if (appState.currentChat?.id === chatId) {
          const updatedChat = { ...appState.currentChat, title: newTitle };
          appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
        }
        
        // Save to backend asynchronously (non-blocking)
        chatPersistenceService.saveChat(chat).catch(error => {
          // console.error('Error saving renamed chat:', error);
        });
      }
    } catch (error) {
      // console.error('Error renaming chat:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to rename chat' });
    } finally {
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    }
  };

  const toggleChatStar = async (chatId: string) => {
    uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' });
    try {
      // Use current local state instead of fetching from API to avoid stale data
      const currentSavedChats = appStateRef.current.savedChats;
      const currentChat = currentSavedChats.find(c => c.id === chatId);
      
      if (!currentChat) {
        console.error(`[toggleChatStar] Chat ${chatId} not found in local state`);
        uiDispatch({ type: 'SHOW_ERROR', payload: 'Chat not found' });
        return;
      }
      
      const wasStarred = currentChat.isStarred;
      
      // console.log(`[toggleChatStar] Chat ${chatId} ${wasStarred ? 'unstarring' : 'starring'}`);
      
      // Call the new dedicated API endpoint
      const result = await chatPersistenceService.toggleChatStar(chatId);
      
      if (!result.success) {
        throw new Error('API returned unsuccessful result');
      }
      
      const newIsStarred = result.isStarred;
      
      // console.log(`[toggleChatStar] Chat ${chatId} starred status: ${wasStarred} -> ${newIsStarred}`);
      
      // Update local state immediately
      const updatedChats = currentSavedChats.map(c => 
        c.id === chatId ? { ...c, isStarred: newIsStarred } : { ...c }
      );
      const sortedChats = chatPersistenceService.sortChatsByTimestamp(updatedChats);
      
      // console.log(`[toggleChatStar] Before state update - starred chats: ${currentSavedChats.filter(c => c.isStarred).length}, unstarred: ${currentSavedChats.filter(c => !c.isStarred).length}`);
      // console.log(`[toggleChatStar] After processing - starred chats: ${sortedChats.filter(c => c.isStarred).length}, unstarred: ${sortedChats.filter(c => !c.isStarred).length}`);
      
      // Force a new array reference to ensure React detects the change
      appDispatch({ type: 'SET_SAVED_CHATS', payload: [...sortedChats] });
      
      // Update current chat if it's the one being toggled
      if (appStateRef.current.currentChat?.id === chatId) {
        const updatedCurrentChat = { ...appStateRef.current.currentChat, isStarred: newIsStarred };
        appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedCurrentChat });
      }
      
    } catch (error) {
      console.error('Error toggling chat star:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to update chat' });
    } finally {
      uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' });
    }
  };

  const filterExperts = (query: string) => {
    if (!query.trim()) {
      appDispatch({ type: 'SET_FILTERED_EXPERTS', payload: appState.experts });
      return;
    }

    const normalizedQuery = query.toLowerCase().trim();
    const filtered = appState.experts.filter(expert => 
      expert.archetype.toLowerCase().includes(normalizedQuery) ||
      expert.description.toLowerCase().includes(normalizedQuery) ||
      expert.background.toLowerCase().includes(normalizedQuery)
    );
    
    appDispatch({ type: 'SET_FILTERED_EXPERTS', payload: filtered });
  };

  const retryMessage = async (messageIndex: number) => {
    if (!appState.currentChat) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'No active chat to retry' });
      return;
    }

    const chatId = appState.currentChat.id;
    const messages = appState.currentChat.messages;
    // console.log('[retryMessage] Total messages before retry:', messages.length);
    
    if (messageIndex < 0 || messageIndex >= messages.length) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Invalid message index' });
      return;
    }
    const aiMessage = messages[messageIndex];
    if (aiMessage.role !== 'expert') {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Can only retry AI responses' });
      return;
    }
    let userMessageIndex = -1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userMessageIndex = i;
        break;
      }
    }
    if (userMessageIndex === -1) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Could not find associated user message' });
      return;
    }
    const userMessage = messages[userMessageIndex];
    // console.log('[retryMessage] Found user message at index:', userMessageIndex);
    
    try {
      stopResponsePolling();
      // Ensure polling is enabled for the retry - directly update ref for immediate visibility
      appStateRef.current.shouldStopPolling = false;
      appStateRef.current.isProcessing = true;
      appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
      appDispatch({ type: 'SET_PROCESSING', payload: true });
      
      // Remove only the AI message onwards (keep the user message)
      // console.log('[retryMessage] Removing messages from index:', messageIndex);
      appDispatch({ 
        type: 'REMOVE_MESSAGES_FROM_INDEX', 
        payload: { chatId, fromIndex: messageIndex } 
      });
      
      // console.log('[retryMessage] Messages after removal:', appStateRef.current.currentChat?.messages.length);
      
      // Get the expert from the current chat or UI state
      const expert = appStateRef.current.currentChat?.expert || uiState.selectedExpert;
      if (!expert) {
        uiDispatch({ type: 'SHOW_ERROR', payload: 'No expert selected for retry' });
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        return;
      }
      
      // Call the API directly (bypass sendMessage to avoid duplication)
      const messageAttachedFiles = userMessage.attachments?.map(att => att.file).filter((file): file is File => file !== undefined) || [];
      
      // Get all files that should be sent: files from this message + all other files attached to this chat
      let allFilesToSend = [...messageAttachedFiles];
      if (appState.currentChat?.allAttachedFiles) {
        appState.currentChat.allAttachedFiles.forEach(existingFile => {
          if (!allFilesToSend.some(f => f.name === existingFile.name && f.size === existingFile.size)) {
            allFilesToSend.push(existingFile);
          }
        });
      }
      
      // Clear previous upload progress
      uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
      
      // Convert files to file references for retry (assume they're already uploaded)
      const fileReferences = allFilesToSend.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        key: `${file.name}-${file.size}` // Fallback key format for retry
      }));
      
      const { requestId } = await apiService.sendMessageWithPreUploadedFiles(
        userMessage.content, 
        expert, 
        fileReferences, 
        chatId, 
        userMessageIndex + 1, 
        (status) => uiDispatch({ type: 'SET_OPERATION_STATUS', payload: status })
      );
      // console.log('[retryMessage] apiService.sendMessage returned requestId:', requestId);
      
      // Check if user cancelled while the API call was in flight
      if (appStateRef.current.shouldStopPolling) {
        // console.log(`[retryMessage] User cancelled during API call for requestId: ${requestId}. Skipping polling.`);
        return;
      }
      
      // CRITICAL: Start polling with robust error handling to ensure it ALWAYS starts
      // console.log(`[retryMessage] Starting polling for requestId: ${requestId}, chatId: ${chatId}`);
      
      // Retry logic to ensure polling starts even if there are transient failures
      let pollingStarted = false;
      let attempts = 0;
      const maxAttempts = 3;
      
             while (!pollingStarted && attempts < maxAttempts) {
         attempts++;
         try {
           // console.log(`[retryMessage] Polling attempt ${attempts}/${maxAttempts} for requestId: ${requestId}`);
           pollForResponse(requestId, chatId, expert);
           pollingStarted = true;
           // console.log(`[retryMessage] Polling successfully started on attempt ${attempts} for requestId: ${requestId}`);
         } catch (pollingError) {
          console.error(`[retryMessage] Polling attempt ${attempts} failed for requestId ${requestId}:`, pollingError);
          if (attempts === maxAttempts) {
            // On final failure, force restart the polling by setting up the minimum state needed
            console.error(`[retryMessage] All polling attempts failed for requestId ${requestId}. Force-starting with minimal state.`);
            try {
              // Reset polling state to ensure clean start
              appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
              appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: requestId });
              appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
              
              // Start polling content directly - this is the core function that must work
              pollForResponseContent(requestId, chatId, expert);
              pollingStarted = true;
              // console.log(`[retryMessage] Force-started polling for requestId: ${requestId}`);
            } catch (forceError) {
              console.error(`[retryMessage] CRITICAL: Even force-start failed for requestId ${requestId}:`, forceError);
              // This should never happen, but if it does, show error and reset processing state
              uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to start response polling. Please try again.' });
              appDispatch({ type: 'SET_PROCESSING', payload: false });
              uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
              return;
            }
          } else {
            // Short delay before retry
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      
    } catch (error) {
      // console.error('Error retrying message:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to retry message' });
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
    }
  };

  /**
   * Edit a user message and resend with new content.
   * Discards the original user message and all subsequent messages, then sends the new content.
   */
  const editAndResendMessage = async (userMessageIndex: number, newContent: string) => {
    if (!appState.currentChat) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'No active chat to edit' });
      return;
    }

    const chatId = appState.currentChat.id;
    const messages = appState.currentChat.messages;

    if (userMessageIndex < 0 || userMessageIndex >= messages.length) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Invalid message index' });
      return;
    }

    const originalMessage = messages[userMessageIndex];
    if (originalMessage.role !== 'user') {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Can only edit user messages' });
      return;
    }

    if (!newContent.trim()) {
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Message cannot be empty' });
      return;
    }

    try {
      stopResponsePolling();
      appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
      appDispatch({ type: 'SET_PROCESSING', payload: true });

      // Remove the original user message and everything after it
      appDispatch({
        type: 'REMOVE_MESSAGES_FROM_INDEX',
        payload: { chatId, fromIndex: userMessageIndex }
      });

      // Get the expert from the current chat or UI state
      const expert = appStateRef.current.currentChat?.expert || uiState.selectedExpert;
      if (!expert) {
        uiDispatch({ type: 'SHOW_ERROR', payload: 'No expert selected' });
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        return;
      }

      const now = new Date().toISOString();

      // Collect file attachments from the original message
      const messageAttachedFiles = originalMessage.attachments?.map(att => att.file).filter((file): file is File => file !== undefined) || [];
      let allFilesToSend = [...messageAttachedFiles];
      if (appStateRef.current.currentChat?.allAttachedFiles) {
        appStateRef.current.currentChat.allAttachedFiles.forEach(existingFile => {
          if (!allFilesToSend.some(f => f.name === existingFile.name && f.size === existingFile.size)) {
            allFilesToSend.push(existingFile);
          }
        });
      }

      // Add new user message with the edited content (keeping original attachments)
      const userMessage: Message = {
        id: Date.now().toString() + '-user',
        content: newContent,
        role: 'user',
        timestamp: now,
        attachments: originalMessage.attachments || []
      };
      appDispatch({ type: 'ADD_MESSAGE', payload: { chatId, message: userMessage } });

      // Add placeholder expert message
      const expertMessage: Message = {
        id: Date.now().toString() + '-expert',
        content: '',
        role: 'expert',
        expertArchetype: expert.name || expert.archetype,
        timestamp: now,
        isStreaming: true
      };
      appDispatch({ type: 'ADD_MESSAGE', payload: { chatId, message: expertMessage } });

      // Clear previous upload progress
      uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });

      // Convert files to file references
      const fileReferences = allFilesToSend.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        key: `${file.name}-${file.size}`
      }));

      // Send with fromMessageIndex = userMessageIndex so backend truncates to before this message
      const { requestId } = await apiService.sendMessageWithPreUploadedFiles(
        newContent,
        expert,
        fileReferences,
        chatId,
        userMessageIndex,
        (status) => uiDispatch({ type: 'SET_OPERATION_STATUS', payload: status })
      );

      // Update the expert message with the requestId
      const updatedExpertMessage = { ...expertMessage, requestId };
      const updatedChat = {
        ...appStateRef.current.currentChat!,
        messages: appStateRef.current.currentChat!.messages.map(msg =>
          msg.id === expertMessage.id ? updatedExpertMessage : msg
        )
      };
      appDispatch({ type: 'SET_CURRENT_CHAT', payload: updatedChat });
      await chatPersistenceService.saveChat(updatedChat);

      // Start polling for the response
      let pollingStarted = false;
      let attempts = 0;
      const maxAttempts = 3;

      while (!pollingStarted && attempts < maxAttempts) {
        attempts++;
        try {
          pollForResponse(requestId, chatId, expert);
          pollingStarted = true;
        } catch (pollingError) {
          console.error(`[editAndResendMessage] Polling attempt ${attempts} failed:`, pollingError);
          if (attempts === maxAttempts) {
            try {
              appDispatch({ type: 'SET_SHOULD_STOP_POLLING', payload: false });
              appDispatch({ type: 'SET_CURRENT_REQUEST_ID', payload: requestId });
              appDispatch({ type: 'SET_CURRENT_CHAT_ID', payload: chatId });
              pollForResponseContent(requestId, chatId, expert);
              pollingStarted = true;
            } catch (forceError) {
              console.error(`[editAndResendMessage] CRITICAL: Force-start failed:`, forceError);
              uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to start response polling. Please try again.' });
              appDispatch({ type: 'SET_PROCESSING', payload: false });
              uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
              return;
            }
          } else {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

    } catch (error) {
      console.error('Error editing and resending message:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to edit and resend message' });
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
    }
  };

  // Initialize the app
  useEffect(() => {
    // console.log('[ChatProvider] useEffect (initialization) running');
    const initialize = async () => {
      try {
        uiDispatch({ type: 'SHOW_LOADING', payload: 'Initializing application...' });
        
        // Start all three operations concurrently
        const expertsPromise = loadExperts();
        const chatsPromise = loadSavedChats();
        const vaultPromise = loadPromptVault();
        
        // Wait only for experts (critical) - chats and vault have their own loading indicators
        try {
          await expertsPromise;
        } catch (error) {
          throw new Error('Failed to load experts');
        }
        
        // Hide main loading overlay - experts loaded and UI is ready
        // Chats continues loading with sidebar spinner, PromptVault with icon spinner
        uiDispatch({ type: 'HIDE_LOADING' });
        // console.log('[ChatProvider] Main UI initialized');
        
        // Handle chats and promptvault completion in background
        chatsPromise.catch(error => {
          console.warn('Failed to load saved chats:', error);
        });
        vaultPromise.catch(error => {
          console.warn('Failed to load prompt vault:', error);
        });
      } catch (error) {
        // console.error('Error during initialization:', error);
        uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to initialize application' });
        uiDispatch({ type: 'HIDE_LOADING' });
      }
    };

    initialize();
  }, []);

  // Handle browser tab closing while streaming
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // If there's an active streaming message, finalize it before the page unloads
      if (appStateRef.current.isProcessing || appStateRef.current.currentExpertMessageId) {
        // console.log('[beforeunload] Finalizing streaming message before page unload');
        
        // Synchronously finalize the streaming message state
        if (appStateRef.current.currentExpertMessageId && appStateRef.current.currentChatId) {
          // Mark the message as complete
          const finalContent = appStateRef.current.displayedContent || '';
          
          // Update the message synchronously in the current chat
          if (appStateRef.current.currentChat) {
            const updatedMessages = appStateRef.current.currentChat.messages.map(msg => {
              if (msg.id === appStateRef.current.currentExpertMessageId) {
                return { ...msg, content: finalContent, isStreaming: false };
              }
              return msg;
            });
            
            const updatedChat = { ...appStateRef.current.currentChat, messages: updatedMessages };
            
            // Try to save the chat synchronously (best effort)
            try {
              // Use navigator.sendBeacon if available for reliable delivery
              if (navigator.sendBeacon) {
                const chatData = JSON.stringify(updatedChat);
                const blob = new Blob([chatData], { type: 'application/json' });
                navigator.sendBeacon('/api/chat/save', blob);
              }
            } catch (error) {
              console.error('Failed to save chat before unload:', error);
            }
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // PromptVault methods
  const loadPromptVault = async (limit: number = 20, force: boolean = false) => {
    // Global session cache - skip API call if already loaded (optimized)
    if (appState.promptVaultLoaded && !force) {
      console.log('PromptVault already loaded in session, using cached data');
      return;
    }
    
    try {
      appDispatch({ type: 'SET_LOADING_PROMPT_VAULT', payload: true });
      const prompts = await apiService.getPromptVault(limit);
      appDispatch({ type: 'SET_PROMPT_VAULT', payload: prompts });
    } catch (error) {
      console.error('Error loading PromptVault:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to load PromptVault' });
    } finally {
      appDispatch({ type: 'SET_LOADING_PROMPT_VAULT', payload: false });
    }
  };

  const savePromptToVault = async (name: string, content: string, tags?: string[], sourceChatId?: string) => {
    try {
      // If no sourceChatId provided, use current chat ID if available
      const chatId = sourceChatId || appState.currentChat?.id;
      
      await apiService.savePromptToVault(name, content, tags, chatId);
      
      // Invalidate cache and reload to get fresh data (optimized - single reload)
      appDispatch({ type: 'INVALIDATE_PROMPT_VAULT_CACHE' });
      
      // Reload the vault after a short delay to ensure OpenSearch consistency
      setTimeout(async () => {
        try {
          await loadPromptVault(20, true); // Force reload after save
        } catch (error) {
          console.error('Error reloading prompt vault:', error);
        }
      }, 1000);
    } catch (error) {
      console.error('Error saving prompt to vault:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to save prompt' });
      throw error;
    }
  };

  const deletePromptFromVault = async (name: string) => {
    try {
      await apiService.deletePromptFromVault(name);
      // Invalidate cache and reload to get fresh data (optimized - single reload)
      appDispatch({ type: 'INVALIDATE_PROMPT_VAULT_CACHE' });
      await loadPromptVault(20, true); // Force reload after delete
    } catch (error) {
      console.error('Error deleting prompt from vault:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to delete prompt' });
      throw error;
    }
  };

  const searchPromptVault = async (searchQuery: string, limit: number = 20): Promise<PromptVaultItem[]> => {
    try {
      const prompts = await apiService.searchPromptVault(searchQuery, limit);
      return prompts;
    } catch (error) {
      console.error('Error searching PromptVault:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to search PromptVault' });
      throw error;
    }
  };

  const updatePromptUsage = async (name: string) => {
    try {
      await apiService.updatePromptUsage(name);
    } catch (error) {
      console.error('Error updating prompt usage:', error);
      // Don't show error to user for usage tracking failures
      // Just log it silently
    }
  };

  const togglePromptStar = async (name: string) => {
    try {
      const result = await apiService.togglePromptStar(name);
      
      if (!result.success) {
        throw new Error('API returned unsuccessful result');
      }
      
      // Update local state immediately
      const updatedPrompts = appState.promptVault.map(prompt => 
        prompt.name === name ? { ...prompt, isStarred: result.isStarred } : prompt
      );
      
      appDispatch({ type: 'SET_PROMPT_VAULT', payload: updatedPrompts });
      
    } catch (error) {
      console.error('Error toggling prompt star:', error);
      uiDispatch({ type: 'SHOW_ERROR', payload: 'Failed to update prompt' });
    }
  };

  const contextValue: ChatContextType = {
    appState,
    uiState,
    loadExperts,
    sendMessage,
    loadChat,
    deleteChat,
    deleteMultipleChats,
    renameChat,
    toggleChatStar,
    setSelectedExpert: (expert) => {
      // Check if we're currently in a chat view and selecting a different expert
      const currentExpert = uiState.selectedExpert;
      const isInChatView = !uiState.isLandingView;
      const currentExpertId = currentExpert?.name || currentExpert?.archetype || currentExpert?.id || currentExpert?.ID;
      const newExpertId = expert?.name || expert?.archetype || expert?.id || expert?.ID;
      const isDifferentExpert = currentExpert && expert && currentExpertId !== newExpertId;
      
      if (isInChatView && isDifferentExpert) {
        // Force user into a new chat by switching to landing view first
        // console.log('[setSelectedExpert] Expert changed in chat view, forcing new chat');
        
        // Stop any active response polling first to finalize streaming messages
        if (appState.isProcessing || appState.currentExpertMessageId) {
          // console.log('[setSelectedExpert] Stopping active response polling');
          stopResponsePolling();
          
          // Save the updated chat with finalized message
          setTimeout(() => {
            if (appStateRef.current.currentChat) {
              saveCurrentChat();
            }
          }, 100);
        }
        
        // Clear current chat from memory
        appDispatch({ type: 'SET_CURRENT_CHAT', payload: null });
        
        // Generate new persistent chat ID for the new chat session
        appDispatch({ type: 'SET_PERSISTENT_CHAT_ID', payload: generateUUID() });
        
        // Reset processing state
        appDispatch({ type: 'SET_PROCESSING', payload: false });
        
        // Clear attached files when starting a new chat
        uiDispatch({ type: 'CLEAR_ATTACHED_FILES' });
        
        // Clear uploaded file references since they're tied to the old chat ID
        uiDispatch({ type: 'CLEAR_UPLOADED_FILE_REFERENCES' });
        
        // Reset operation status
        uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
        
        // Hide all chats view if it's open
        uiDispatch({ type: 'HIDE_ALL_CHATS_VIEW' });
        
        // Switch to landing view
        uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: true });
      }
      
      // Save selected expert to localStorage
      if (expert) {
        try {
          const expertId = expert.name || expert.archetype || expert.id || expert.ID;
          if (expertId) {
            localStorage.setItem('selectedExpertId', expertId);
          }
        } catch (error) {
          console.warn('Failed to save selected expert to localStorage:', error);
        }
      }
      
      // Set the new expert
      uiDispatch({ type: 'SET_SELECTED_EXPERT', payload: expert });
    },
    switchToLanding: () => {
      // Stop any active response polling first to finalize streaming messages
      if (appState.isProcessing || appState.currentExpertMessageId) {
        // console.log('[switchToLanding] Stopping active response polling');
        stopResponsePolling();
        
        // Save the updated chat with finalized message
        setTimeout(() => {
          if (appStateRef.current.currentChat) {
            saveCurrentChat();
          }
        }, 100);
      }
      
      // Clear current chat from memory (matching previous implementation)
      appDispatch({ type: 'SET_CURRENT_CHAT', payload: null });
      
      // Generate new persistent chat ID for the new chat session
      appDispatch({ type: 'SET_PERSISTENT_CHAT_ID', payload: generateUUID() });
      
      // Reset processing state
      appDispatch({ type: 'SET_PROCESSING', payload: false });
      
      // Clear attached files when starting a new chat (matching previous implementation)
      uiDispatch({ type: 'CLEAR_ATTACHED_FILES' });
      
      // Clear uploaded file references since they're tied to the old chat ID
      uiDispatch({ type: 'CLEAR_UPLOADED_FILE_REFERENCES' });
      
      // Reset operation status
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: '' });
      
      // Hide all chats view if it's open (matching previous implementation's hideChatHistoryView)
      uiDispatch({ type: 'HIDE_ALL_CHATS_VIEW' });
      
      // Switch to landing view
      uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: true });
    },
    switchToChat: () => uiDispatch({ type: 'SET_IS_LANDING_VIEW', payload: false }),
    toggleSidebar: () => {
      const newCollapsedState = !uiStateRef.current.sidebarCollapsed;
      
      // Save to localStorage
      try {
        localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsedState));
      } catch (error) {
        console.warn('Failed to save sidebar state to localStorage:', error);
      }
      
      uiDispatch({ type: 'TOGGLE_SIDEBAR' });
    },
    toggleRightSidebar: () => uiDispatch({ type: 'TOGGLE_RIGHT_SIDEBAR' }),
    addAttachedFile: (file) => {
      // Add file to attached files list
      uiDispatch({ type: 'ADD_ATTACHED_FILE', payload: file });
      
      // Start instant upload
      (async () => {
        const uploadedKey = await uploadFileInstantly(file);
        if (uploadedKey) {
          // Store the uploaded file reference
          uiDispatch({ type: 'ADD_UPLOADED_FILE_REFERENCE', payload: { file, key: uploadedKey } });
        }
      })();
    },
    removeAttachedFile: (index) => {
      // Get the file being removed
      const fileToRemove = uiState.attachedFiles[index];
      
      if (fileToRemove) {
        // Remove from attached files
        uiDispatch({ type: 'REMOVE_ATTACHED_FILE', payload: index });
        
        // Find and remove from uploaded file references
        const uploadedIndex = uiState.uploadedFileReferences.findIndex(
          ref => ref.file.name === fileToRemove.name && ref.file.size === fileToRemove.size
        );
        if (uploadedIndex >= 0) {
          uiDispatch({ type: 'REMOVE_UPLOADED_FILE_REFERENCE', payload: uploadedIndex });
        }
        
        // Remove from upload progress tracking
        const newProgressMap = new Map(uiState.fileUploadProgress);
        newProgressMap.delete(fileToRemove.name);
        const newCompletedSet = new Set(uiState.completedFileUploads);
        newCompletedSet.delete(fileToRemove.name);
        
        // Clear progress if this was the only file
        if (newProgressMap.size === 0) {
          uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
        }
      }
    },
    clearAttachedFiles: () => {
      uiDispatch({ type: 'CLEAR_ATTACHED_FILES' });
      uiDispatch({ type: 'CLEAR_UPLOADED_FILE_REFERENCES' });
      // Also clear upload progress when clearing all files
      uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
    },
    showLoading: (message) => uiDispatch({ type: 'SHOW_LOADING', payload: message }),
    hideLoading: () => uiDispatch({ type: 'HIDE_LOADING' }),
    showError: (message) => uiDispatch({ type: 'SHOW_ERROR', payload: message }),
    hideError: () => uiDispatch({ type: 'HIDE_ERROR' }),
    stopResponsePolling,
    filterExperts,
    showAllChatsView: () => uiDispatch({ type: 'SHOW_ALL_CHATS_VIEW' }),
    hideAllChatsView: () => uiDispatch({ type: 'HIDE_ALL_CHATS_VIEW' }),
    showSidebarLoading: () => uiDispatch({ type: 'SHOW_SIDEBAR_LOADING' }),
    hideSidebarLoading: () => uiDispatch({ type: 'HIDE_SIDEBAR_LOADING' }),
    retryMessage,
    editAndResendMessage,
    registerSpacerUpdateCallback: (callback) => {
      spacerUpdateCallback.current = callback;
    },
    unregisterSpacerUpdateCallback: () => {
      spacerUpdateCallback.current = null;
    },
    setOperationStatus: (status) => {
      // console.log('ChatContext: Setting operation status to:', status);
      uiDispatch({ type: 'SET_OPERATION_STATUS', payload: status });
    },
    getFileUploadProgress: (fileName: string) => {
      return uiState.fileUploadProgress.get(fileName) || 0;
    },
    isFileUploadComplete: (fileName: string) => {
      return uiState.completedFileUploads.has(fileName);
    },
    areAllUploadsComplete: () => {
      // Check if all files with progress are completed
      const allFilesWithProgress = Array.from(uiState.fileUploadProgress.keys());
      return allFilesWithProgress.length > 0 && allFilesWithProgress.every(fileName => 
        uiState.completedFileUploads.has(fileName)
      );
    },
    clearFileUploadProgress: () => {
      uiDispatch({ type: 'CLEAR_FILE_UPLOAD_PROGRESS' });
    },
    hasUploadsInProgress: () => {
      // Check if any file upload is in progress (not complete)
      const allFilesWithProgress = Array.from(uiState.fileUploadProgress.keys());
      return allFilesWithProgress.some(fileName => 
        !uiState.completedFileUploads.has(fileName) && uiState.fileUploadProgress.get(fileName)! < 100
      );
    },
    validateCurrentMessage: async (messageText: string) => {
      if (!uiState.selectedExpert) {
        return { isValid: true };
      }
      
      const modelName = getModelFromExpert(uiState.selectedExpert);
      const validationResult = await validateMessage(messageText, uiState.attachedFiles, modelName);
      
      if (!validationResult.isValid && validationResult.errorMessage) {
        uiDispatch({ type: 'SET_VALIDATION_ERROR', payload: validationResult.errorMessage });
        uiDispatch({ type: 'SHOW_VALIDATION_ERROR' });
      } else {
        uiDispatch({ type: 'HIDE_VALIDATION_ERROR' });
      }
      
      return validationResult;
    },
    clearValidationError: () => {
      uiDispatch({ type: 'HIDE_VALIDATION_ERROR' });
    },
    // PromptVault methods
    loadPromptVault,
    savePromptToVault,
    deletePromptFromVault,
    searchPromptVault,
    updatePromptUsage,
    togglePromptStar,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}; 